import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixInvoicesInvoicesV4Invoice from './invoices-invoices-v4-invoice-invoices.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

/** A request for payment issued by a business to a customer, containing line items, taxes, discounts, and payment terms. */
export interface Invoice {
  /**
   * Invoice ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the invoice is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the invoice.
   *
   * Ignored when creating an invoice.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the invoice was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the invoice was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Invoice status.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Additional flags providing context about the invoice's state, independent of its primary status. These flags are system-managed and read-only.
   * @readonly
   * @maxSize 2
   */
  statusQualifiers?: StatusQualifierWithLiterals[];
  /**
   * Actions that can be performed on this invoice based on its current status.
   * @readonly
   * @maxSize 12
   */
  availableActions?: ActionWithLiterals[];
  /**
   * Information about the app that created the invoice.
   * @immutable
   */
  sourceReference?: SourceReference;
  /**
   * Invoice type and Wix eCommerce association. May change when an eligible legacy invoice is converted.
   * @immutable
   */
  reference?: Reference;
  /** Regional properties for the invoice. When not provided, defaults are taken from the [Site Properties API](https://dev.wix.com/docs/api-reference/business-management/site-properties/introduction). */
  regionalProperties?: RegionalProperties;
  /**
   * Invoice numbering. Assigned when the invoice is published.
   * @readonly
   */
  numbering?: Numbering;
  /** Date the invoice was issued. */
  issueDate?: Date | null;
  /** Date when payment is due. Cannot be before `issueDate`. */
  dueDate?: Date | null;
  /**
   * Invoice title.
   * @minLength 1
   * @maxLength 200
   */
  title?: string | null;
  /**
   * Currency code for all monetary amounts on the invoice.
   * Three-letter code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * Set at creation and can't be changed.
   * @format CURRENCY
   * @immutable
   */
  currency?: string;
  /** Customer information including contact details and billing address. */
  customerInfo?: CustomerInfo;
  /** Shipping destination for the invoice, including the recipient's contact details and address. Can exist independently without a shipment. When a shipment is configured, `shipmentInfo` uses this address as the delivery destination unless a pickup address is specified. */
  shippingInfo?: ShippingInfo;
  /** Business details displayed on the invoice, such as company name, address, email, phone, and logo. Automatically populated from the [Site Properties API](https://dev.wix.com/docs/api-reference/business-management/site-properties/introduction) when the invoice is created, if not explicitly provided. These details aren't synced with Site Properties after creation and become permanently fixed once `status` reaches `PARTIALLY_PAID`, `PAID`, or `VOIDED`. */
  businessDetails?: BusinessDetails;
  /**
   * Line items on the invoice.
   * @maxSize 300
   */
  lineItems?: LineItem[];
  /** Delivery and shipping cost details (carrier, method, price, tax) tied to the Wix eCommerce integration. Defaults to using `shippingInfo.shippingAddress` as the delivery destination unless `shipmentInfo.logistics.pickupDetails` is specified. */
  shipmentInfo?: ShipmentInfo;
  /**
   * Additional fees applied to the invoice.
   * @maxSize 20
   */
  additionalFees?: AdditionalFee[];
  /**
   * Invoice-level discounts.
   * @maxSize 20
   */
  discounts?: Discount[];
  /** Deposit payment configuration. */
  deposit?: Deposit;
  /**
   * Tax information.
   * @readonly
   */
  taxInfo?: TaxInfo;
  /**
   * Payment records.
   * @maxSize 100
   * @readonly
   */
  payments?: Payment[];
  /**
   * Financial summary including subtotal, taxes, and total amount.
   * @readonly
   */
  totals?: Totals;
  /**
   * Whether the invoice is archived.
   * @readonly
   */
  archived?: boolean;
  /** Custom fields displayed on the invoice document. */
  customFields?: CustomFields;
  /** Invoice preset applied to this invoice. Determines the appearance and custom fields on the invoice document. If not provided, the [default preset](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoice-presets/set-default-invoice-preset) is used. */
  presetProperties?: PresetProperties;
  /**
   * Files attached to the invoice.
   * @maxSize 5
   */
  attachments?: Attachment[];
  /**
   * PDF document generation status.
   * @readonly
   */
  documentInfo?: DocumentInfo;
  /**
   * Invoice URL.
   * @readonly
   */
  links?: Links;
  /** Business location. */
  businessLocation?: BusinessLocation;
  /**
   * Read-only record of when the invoice was last sent and last viewed.
   * @readonly
   */
  activityInfo?: ActivityInfo;
  /**
   * Whether the invoice contains subscription line items. `true` when at least one line item carries subscription info.
   * @readonly
   */
  hasSubscriptions?: boolean;
  /** Tags for organizing invoices. `publicTags` are visible to anyone with access to the invoice. `privateTags` are restricted to site owners and require additional permissions to access. */
  tags?: Tags;
}

export enum Status {
  /** Invoice is in draft status. Can be edited before publishing. */
  DRAFT = 'DRAFT',
  /** Invoice number is being allocated. */
  PUBLISHING = 'PUBLISHING',
  /** Invoice is published and ready for payment. */
  PUBLISHED = 'PUBLISHED',
  /** Invoice has received at least one payment but isn't fully paid. */
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  /** Invoice is fully paid. */
  PAID = 'PAID',
  /** Invoice is voided and no longer valid. */
  VOIDED = 'VOIDED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'DRAFT'
  | 'PUBLISHING'
  | 'PUBLISHED'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'VOIDED';

export enum StatusQualifier {
  /** Invoice was sent to a customer. */
  SENT = 'SENT',
  /** Mark Viewed was called for the invoice. */
  VIEWED = 'VIEWED',
  /** Invoice `dueDate` has passed and the invoice isn't fully paid or voided. */
  OVERDUE = 'OVERDUE',
}

/** @enumType */
export type StatusQualifierWithLiterals =
  | StatusQualifier
  | 'SENT'
  | 'VIEWED'
  | 'OVERDUE';

export enum Action {
  /** Invoice can be deleted. Available when invoice is in `DRAFT` status. */
  DELETE = 'DELETE',
  /** Invoice can be updated. Available when invoice is in `DRAFT`, `PUBLISHED`, or `PARTIALLY_PAID` status. */
  UPDATE = 'UPDATE',
  /** Invoice can be published. Available when invoice is in `DRAFT` status. */
  PUBLISH = 'PUBLISH',
  /** Payment can be recorded. Available when invoice is in `PUBLISHED` or `PARTIALLY_PAID` status. */
  PAY = 'PAY',
  /** Invoice can be voided. Available when invoice is in `PUBLISHED` or `PARTIALLY_PAID` status. */
  VOID = 'VOID',
  /** Invoice can be sent. Available when invoice is in `PUBLISHED` or later status. */
  SEND = 'SEND',
  /** Invoice can be marked as viewed. Available when invoice isn't in `DRAFT` status. */
  MARK_AS_VIEWED = 'MARK_AS_VIEWED',
  /** PDF document can be generated. Not available while another PDF is being processed. */
  GENERATE_PDF_DOCUMENT = 'GENERATE_PDF_DOCUMENT',
  /** Invoice can be archived. Available when invoice isn't in `DRAFT` status and isn't already archived. */
  ARCHIVE = 'ARCHIVE',
  /** Invoice can be unarchived. Available when invoice is archived. */
  UNARCHIVE = 'UNARCHIVE',
  /** Invoice can be marked as sent. Available when invoice isn't in `DRAFT` status. */
  MARK_AS_SENT = 'MARK_AS_SENT',
  /** Invoice can be converted into the standard standalone or auto-charge flow. Available for eligible legacy invoices without payments or an initialized payment flow. */
  CONVERT = 'CONVERT',
}

/** @enumType */
export type ActionWithLiterals =
  | Action
  | 'DELETE'
  | 'UPDATE'
  | 'PUBLISH'
  | 'PAY'
  | 'VOID'
  | 'SEND'
  | 'MARK_AS_VIEWED'
  | 'GENERATE_PDF_DOCUMENT'
  | 'ARCHIVE'
  | 'UNARCHIVE'
  | 'MARK_AS_SENT'
  | 'CONVERT';

export interface SourceReference {
  /**
   * ID of the app that created the invoice.
   * @format GUID
   */
  appId?: string;
  /**
   * Developer-provided ID for linking this invoice to a record in an external system. For example, an order ID in a third-party platform.
   * @format GUID
   */
  externalReferenceId?: string | null;
  /**
   * Additional metadata provided by the source app. Used for passing app-specific context
   * that does not fit into the standard fields, such as internal IDs or configuration details.
   * @maxSize 10
   */
  externalProperties?: Record<string, string>;
}

/** Reference linking the invoice to a Wix eCommerce order or standalone context. */
export interface Reference extends ReferenceReferenceOptionsOneOf {
  /** Standalone invoice details. Wix eCommerce order is assigned when payment is initiated. */
  standaloneReference?: Standalone;
  /** Invoice reference to the existing Wix eCommerce order. */
  orderReference?: Order;
  /** Reference for invoices migrated from an older invoicing system. */
  migratedReference?: Migrated;
  /** Auto-charge invoice details. Wix eCommerce order is assigned when payment is initiated. */
  autoChargeReference?: AutoCharge;
  /** Invoice type. */
  referenceType?: ReferenceTypeWithLiterals;
}

/** @oneof */
export interface ReferenceReferenceOptionsOneOf {
  /** Standalone invoice details. Wix eCommerce order is assigned when payment is initiated. */
  standaloneReference?: Standalone;
  /** Invoice reference to the existing Wix eCommerce order. */
  orderReference?: Order;
  /** Reference for invoices migrated from an older invoicing system. */
  migratedReference?: Migrated;
  /** Auto-charge invoice details. Wix eCommerce order is assigned when payment is initiated. */
  autoChargeReference?: AutoCharge;
}

export enum ReferenceType {
  /** Independent invoice. An eCommerce order is created automatically when the first payment is made. */
  STANDALONE = 'STANDALONE',
  /** Invoice created for an existing Wix eCommerce order. Lifecycle is bound to that order. */
  ORDER = 'ORDER',
  /** Invoice migrated from an older invoicing system. */
  MIGRATED = 'MIGRATED',
  /**
   * Auto-charged invoice. Required for invoices carrying subscription line items.
   * @documentationMaturity preview
   * @internal
   */
  AUTO_CHARGE = 'AUTO_CHARGE',
}

/** @enumType */
export type ReferenceTypeWithLiterals =
  | ReferenceType
  | 'STANDALONE'
  | 'ORDER'
  | 'MIGRATED';

export interface Standalone {
  /**
   * Wix eCommerce order ID. Assigned automatically when payments are enabled.
   * @readonly
   * @format GUID
   */
  orderId?: string | null;
  /**
   * Whether to restrict modification actions to the app that created the invoice.
   * @immutable
   */
  limitActions?: boolean;
}

export interface Order {
  /**
   * Wix eCommerce order ID.
   * @format GUID
   */
  orderId?: string;
  /**
   * Wix eCommerce order number.
   * @min 1
   */
  orderNumber?: string | null;
}

/** Details for invoices migrated from an older invoicing system. */
export interface Migrated {
  /**
   * Wix eCommerce order ID. Assigned automatically when payments are enabled.
   * @readonly
   * @format GUID
   */
  orderId?: string | null;
  /**
   * Limit changes to the application identified by `sourceReference.appId` only. If set to true, the invoice will be locked for direct actions: `PUBLISH`, `UPDATE`, `DELETE`.
   * @immutable
   */
  limitActions?: boolean;
  /**
   * Wix eCommerce order number.
   * @readonly
   * @min 1
   */
  orderNumber?: string | null;
}

/** Auto-charge invoice details. Behaves like a standalone invoice; required for subscription line items. */
export interface AutoCharge {
  /**
   * Wix eCommerce order ID. Assigned automatically when payments are enabled.
   * @readonly
   * @format GUID
   */
  orderId?: string | null;
  /**
   * Whether to restrict modification actions to the app that created the invoice.
   * @immutable
   */
  limitActions?: boolean;
}

export interface RegionalProperties {
  /** Locale used for formatting numbers, dates, and times. */
  locale?: Locale;
  /**
   * Time zone in UTC offset or IANA format.
   * @minLength 1
   * @maxLength 32
   */
  timeZone?: string | null;
  /**
   * Invoice language in ISO 639-1 format.
   * @format LANGUAGE
   */
  language?: string;
}

export interface Locale {
  /**
   * 2-letter language code in ISO 639-1 format.
   * @format LANGUAGE
   */
  languageCode?: string;
  /**
   * 2-letter country code in ISO 3166 format.
   * @format COUNTRY
   */
  country?: string | null;
}

/** Assigned invoice number. This is set automatically when the invoice is published, based on the numbering configuration in the [Invoices Settings API](https://dev.wix.com/docs/rest/business-management/get-paid/invoices/invoices-settings/introduction). */
export interface Numbering {
  /** Sequential invoice number. */
  number?: number | null;
  /**
   * Prefix before the invoice number.
   * @minLength 1
   * @maxLength 10
   */
  prefix?: string | null;
  /**
   * Suffix after the invoice number.
   * @minLength 1
   * @maxLength 10
   */
  suffix?: string | null;
  /**
   * Full display number in `[prefix][number][suffix]` format.
   * @maxLength 30
   */
  displayNumber?: string;
}

/** Customer information. */
export interface CustomerInfo {
  /**
   * ID of the customer's contact in the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction).
   * @format GUID
   */
  contactId?: string | null;
  /** Customer's contact details including name, email, and phone. */
  contactDetails?: FullAddressContactDetails;
  /** Customer's billing address. */
  billingAddress?: Address;
}

/** Full contact details for an address */
export interface FullAddressContactDetails {
  /**
   * Contact's first name.
   * @maxLength 100
   */
  firstName?: string | null;
  /**
   * Contact's last name.
   * @maxLength 100
   */
  lastName?: string | null;
  /**
   * Contact's full name.
   * @internal
   * @maxLength 200
   */
  fullName?: string | null;
  /**
   * Contact's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /**
   * Contact's company name.
   * @maxLength 100
   */
  company?: string | null;
  /**
   * Email associated with the address.
   * @format EMAIL
   */
  email?: string | null;
  /** Tax info. Currently usable only in Brazil. */
  vatId?: VatId;
}

export interface VatId {
  /**
   * Customer's tax ID.
   * @maxLength 100
   */
  _id?: string;
  /**
   * Tax type.
   *
   * Supported values:
   * + `CPF`: for individual tax payers
   * + `CNPJ`: for corporations
   */
  type?: VatTypeWithLiterals;
}

/** tax info types */
export enum VatType {
  UNSPECIFIED = 'UNSPECIFIED',
  /** CPF - for individual tax payers. */
  CPF = 'CPF',
  /** CNPJ - for corporations */
  CNPJ = 'CNPJ',
}

/** @enumType */
export type VatTypeWithLiterals = VatType | 'UNSPECIFIED' | 'CPF' | 'CNPJ';

/** Physical address */
export interface Address {
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 50
   */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 50
   */
  postalCode?: string | null;
  /** Street address. */
  streetAddress?: StreetAddress;
  /**
   * Main address line (usually street name and number).
   * @maxLength 150
   */
  addressLine1?: string | null;
  /**
   * Free text providing more detailed address info. Usually contains apt, suite, floor.
   * @maxLength 100
   */
  addressLine2?: string | null;
  /**
   * Free text to help find the address.
   * @maxLength 100
   */
  hint?: string | null;
  /** Geocode object containing latitude and longitude coordinates. */
  location?: AddressLocation;
  /**
   * Country's full name.
   * @readonly
   */
  countryFullname?: string | null;
  /**
   * Subdivision full-name.
   * @readonly
   */
  subdivisionFullname?: string | null;
}

export interface StreetAddress {
  /**
   * Street number.
   * @maxLength 50
   */
  number?: string;
  /**
   * Street name.
   * @maxLength 200
   */
  name?: string;
  /**
   * Apartment number.
   * @internal
   * @maxLength 50
   */
  apt?: string;
  /**
   * Optional address line 1
   * @internal
   * @maxLength 300
   */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

/** Shipping recipient information. */
export interface ShippingInfo {
  /** Recipient's contact details. */
  contactDetails?: FullAddressContactDetails;
  /** Recipient's shipping address. */
  shippingAddress?: Address;
}

/** Business details displayed on the invoice. */
export interface BusinessDetails {
  /**
   * Business name.
   * @minLength 1
   * @maxLength 200
   */
  companyName?: string | null;
  /**
   * Business email.
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Business phone.
   * @format PHONE
   */
  phone?: string | null;
  /** Business address. */
  address?: Address;
  /**
   * Wix Media image ID for the business logo.
   * @minLength 1
   * @maxLength 100
   */
  imageId?: string | null;
  /**
   * Company registration number or other business identifier displayed on the invoice.
   * @maxLength 30
   */
  companyId?: string | null;
}

/** An item on the invoice. */
export interface LineItem extends LineItemLineItemOptionsOneOf {
  /** Custom item. */
  customItem?: CustomItem;
  /** Catalog item. */
  catalogItem?: CatalogItem;
  /** Line item type. */
  lineItemType?: LineItemTypeWithLiterals;
}

/** @oneof */
export interface LineItemLineItemOptionsOneOf {
  /** Custom item. */
  customItem?: CustomItem;
  /** Catalog item. */
  catalogItem?: CatalogItem;
}

export enum LineItemType {
  /** Custom line item with manually specified details. */
  CUSTOM = 'CUSTOM',
  /** Line item from a product catalog. */
  CATALOG = 'CATALOG',
}

/** @enumType */
export type LineItemTypeWithLiterals = LineItemType | 'CUSTOM' | 'CATALOG';

export interface CustomItem {
  /**
   * Item ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Item name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /**
   * Item description.
   * @minLength 1
   * @maxLength 600
   */
  description?: string | null;
  /**
   * Number of items.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:100000, maxScale:4 }
   */
  quantity?: string;
  /**
   * Price per item.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  price?: string;
  /** References to the line item's origin catalog. */
  catalogReference?: CatalogReference;
  /**
   * Line item discounts.
   * @maxSize 2
   */
  discounts?: LineItemDiscount[];
  /** Line item tax info. */
  taxInfo?: LineItemTaxInfo;
  /**
   * Line item totals.
   * @readonly
   */
  totals?: LineItemTotals;
  /**
   * Subscription configuration. Line item subscription starts after invoice is fully paid. Only one subscription line item is supported per invoice.
   * @internal
   */
  subscriptionInfo?: SubscriptionInfo;
}

export interface CatalogReference {
  /**
   * ID of the item in the catalog.
   * @format GUID
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * For items from Wix catalogs, the following values always apply:
   *
   * - Wix Stores: `215238eb-22a5-4c36-9e7b-e7c08025e04e`
   * - Wix Bookings: `13d21c63-b5ec-5912-8397-c3a5ddb27a97`
   * - Wix Restaurants: `9a5d83fd-8570-482e-81ab-cfa88942ee60`
   * @format GUID
   */
  appId?: string;
  /** Additional item details in key-value pairs. Values differ depending on the catalog. */
  options?: Record<string, any> | null;
}

export interface LineItemDiscount extends LineItemDiscountDiscountOptionsOneOf {
  /**
   * Fixed discount amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  fixedDiscount?: string;
  /**
   * Percentage discount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:100, maxScale:2 }
   */
  percentageDiscount?: string;
  /**
   * Discount ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Discount name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Type of the discount. */
  discountType?: LineItemDiscountDiscountTypeWithLiterals;
}

/** @oneof */
export interface LineItemDiscountDiscountOptionsOneOf {
  /**
   * Fixed discount amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  fixedDiscount?: string;
  /**
   * Percentage discount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:100, maxScale:2 }
   */
  percentageDiscount?: string;
}

export enum LineItemDiscountDiscountType {
  /** Fixed monetary amount. */
  FIXED = 'FIXED',
  /** Percentage-based discount. */
  PERCENTAGE = 'PERCENTAGE',
}

/** @enumType */
export type LineItemDiscountDiscountTypeWithLiterals =
  | LineItemDiscountDiscountType
  | 'FIXED'
  | 'PERCENTAGE';

export interface LineItemTaxInfo {
  /**
   * Reference to a tax group that defines the tax rules applicable to this line item.
   * Use the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.
   * If not specified, the default tax rules for the business will apply.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Tax breakdown for this line item.
   * @readonly
   * @maxSize 7
   */
  taxBreakdown?: LineItemTaxBreakdown[];
  /**
   * Whether tax is included in the item price.
   * @readonly
   */
  taxIncludedInPrice?: boolean | null;
}

export interface LineItemTaxBreakdown {
  /**
   * Tax name.
   * @maxLength 200
   */
  name?: string;
  /**
   * Tax rate as a decimal. For example, `0.13` for 13%.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string;
  /**
   * Amount subject to this tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  taxableAmount?: string;
  /**
   * Amount exempt from this tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  exemptAmount?: string;
  /**
   * Tax amount applied.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  taxAmount?: string;
}

export interface LineItemTotals {
  /**
   * Subtotal before discounts and taxes.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  subtotal?: string | null;
  /**
   * Total discount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  discount?: string | null;
  /**
   * Total price before discounts.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  priceBeforeDiscount?: string | null;
  /**
   * Total price after discounts and before taxes.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  priceBeforeTax?: string | null;
  /**
   * Total tax-exempt amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  exempt?: string | null;
  /**
   * Total tax amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  tax?: string | null;
  /**
   * Total price after taxes and discounts.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  priceAfterTax?: string | null;
}

/** Subscription configuration for a line item. When present, the line item creates a subscription after the invoice is fully paid. */
export interface SubscriptionInfo {
  /**
   * Wix eCommerce subscription ID. Available once payment is initiated.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /** Subscription billing cycle that defines how often a new invoice is produced. */
  cycle?: Cycle;
  /**
   * The billing cycle number this invoice represents in the subscription sequence. For example, 1 for the first invoice, 2 for the second.
   * @min 1
   * @max 1000000
   */
  currentCycle?: number;
  /**
   * Total number of billing cycles. When set to 0, the subscription runs indefinitely. When set to a positive value, the subscription ends after that many cycles.
   * @max 500
   */
  numberOfCycles?: number;
  /** The start date of subscription. If not defined, subscription starts immediately after first invoice is paid. */
  startDate?: Date | null;
  /**
   * Billing subscription ID. Populated after the invoice is fully paid.
   * @readonly
   * @format GUID
   */
  billingSubscriptionId?: string | null;
}

/** Billing frequency for the subscription. Set one of weekly, monthly , or yearly to specify how often a new invoice is generated. For weekly cycles, the billing day of the week is derived from start_date. For monthly cycles, set day_of_month . */
export interface Cycle extends CycleCycleOptionsOneOf {
  /** Recurs every week. The billing weekday is derived from the subscription start date. */
  weekly?: Weekly;
  /** Recurs every month on a given day of month. */
  monthly?: Monthly;
  /** Recurs every year. The billing month and day are derived from the subscription start date. */
  yearly?: Yearly;
  /**
   * Interval multiplier for the billing frequency. For example, a weekly cycle with interval 3 recurs every 3 weeks. A monthly cycle with interval 3 recurs every 3 months. A yearly cycle with interval 2 recurs every 2 years.
   * @min 1
   * @max 30
   */
  interval?: number | null;
}

/** @oneof */
export interface CycleCycleOptionsOneOf {
  /** Recurs every week. The billing weekday is derived from the subscription start date. */
  weekly?: Weekly;
  /** Recurs every month on a given day of month. */
  monthly?: Monthly;
  /** Recurs every year. The billing month and day are derived from the subscription start date. */
  yearly?: Yearly;
}

/** Weekly subscription cycle. No additional configuration needed — use interval to bill every N weeks. */
export interface Weekly {}

/** Monthly subscription cycle settings. */
export interface Monthly {
  /**
   * Day of month.
   * @min 1
   * @max 28
   */
  dayOfMonth?: number;
}

/** Yearly subscription cycle settings. The billing month and day are derived from the subscription start date. */
export interface Yearly {}

export interface CatalogItem {
  /**
   * Item ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Item name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /**
   * Item description.
   * @minLength 1
   * @maxLength 600
   */
  description?: string | null;
  /**
   * Number of items.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:100000, maxScale:4 }
   */
  quantity?: string;
  /**
   * Price override. If not provided, the catalog price is used.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  price?: string | null;
  /** References to the line item's origin catalog. */
  catalogReference?: CatalogReference;
  /**
   * Item type.
   * @readonly
   */
  itemType?: ItemType;
  /**
   * Line item discounts.
   * @maxSize 2
   */
  discounts?: LineItemDiscount[];
  /** Line item tax info. */
  taxInfo?: LineItemTaxInfo;
  /**
   * Line item totals.
   * @readonly
   */
  totals?: LineItemTotals;
  /**
   * Subscription configuration. Line item subscription starts after invoice is fully paid. Only one subscription line item is supported per invoice.
   * @internal
   */
  subscriptionInfo?: SubscriptionInfo;
}

export interface ItemType extends ItemTypeItemTypeDataOneOf {
  /** Preset item type. */
  preset?: PresetTypeWithLiterals;
  /**
   * Custom item type.
   * @maxLength 200
   */
  custom?: string;
}

/** @oneof */
export interface ItemTypeItemTypeDataOneOf {
  /** Preset item type. */
  preset?: PresetTypeWithLiterals;
  /**
   * Custom item type.
   * @maxLength 200
   */
  custom?: string;
}

export enum PresetType {
  /** Service item. */
  SERVICE = 'SERVICE',
  /** Gift card item. */
  GIFT_CARD = 'GIFT_CARD',
  /** Digital item. */
  DIGITAL = 'DIGITAL',
  /** Physical item. */
  PHYSICAL = 'PHYSICAL',
}

/** @enumType */
export type PresetTypeWithLiterals =
  | PresetType
  | 'SERVICE'
  | 'GIFT_CARD'
  | 'DIGITAL'
  | 'PHYSICAL';

export interface ShipmentInfo {
  /**
   * Shipping carrier ID.
   * @format GUID
   */
  carrierId?: string | null;
  /**
   * Shipping method code.
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  /**
   * Shipping method title.
   * @minLength 1
   * @maxLength 250
   */
  title?: string | null;
  /** Delivery logistics details. */
  logistics?: DeliveryLogistics;
  /** Shipping region. */
  region?: ShipmentRegion;
  /**
   * Shipping price.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  price?: string | null;
  /** Tax information for shipping. */
  taxInfo?: ShipmentTaxInfo;
  /**
   * Shipping cost totals.
   * @readonly
   */
  totals?: ShipmentTotals;
}

export interface DeliveryLogistics {
  /**
   * Expected delivery time as free text.
   * @minLength 1
   * @maxLength 500
   */
  deliveryTime?: string | null;
  /** Expected delivery time window. */
  deliveryTimeSlot?: DeliveryTimeSlot;
  /**
   * Carrier delivery instructions.
   * @minLength 1
   * @maxLength 1000
   */
  instructions?: string | null;
  /** Pickup address override. If not provided, the delivery destination defaults to the invoice's `shippingInfo.shippingAddress`. */
  pickupDetails?: PickupDetails;
}

export interface DeliveryTimeSlot {
  /** Start of the delivery window. */
  from?: Date | null;
  /** End of the delivery window. */
  to?: Date | null;
}

export interface PickupDetails {
  /** Pickup address. */
  address?: Address;
  /** Pickup method. */
  pickupMethod?: PickupMethodWithLiterals;
}

export enum PickupMethod {
  PICKUP_POINT = 'PICKUP_POINT',
  STORE_PICKUP = 'STORE_PICKUP',
}

/** @enumType */
export type PickupMethodWithLiterals =
  | PickupMethod
  | 'PICKUP_POINT'
  | 'STORE_PICKUP';

export interface ShipmentRegion {
  /**
   * Region name.
   * @minLength 1
   * @maxLength 100
   */
  name?: string | null;
}

export interface ShipmentTaxInfo {
  /**
   * Reference to a tax group that defines the tax rules applicable to the shipment.
   * Use the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.
   * If not specified, the default tax rules for the business will apply.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Tax breakdown for shipping.
   * @readonly
   * @maxSize 7
   */
  taxBreakdown?: ShipmentTaxBreakdown[];
  /**
   * Whether tax is included in the shipping price.
   * @readonly
   */
  taxIncludedInPrice?: boolean | null;
}

export interface ShipmentTaxBreakdown {
  /**
   * Tax name.
   * @maxLength 200
   */
  name?: string;
  /**
   * Tax rate as a decimal. For example, `0.13` for 13%.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string;
  /**
   * Taxable amount this tax applies to.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  taxableAmount?: string;
  /**
   * Amount of the price that was exempt from tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  exemptAmount?: string;
  /**
   * Applied tax amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  taxAmount?: string;
}

export interface ShipmentTotals {
  /**
   * Shipping discount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  discount?: string | null;
  /**
   * Shipping price before tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  priceBeforeTax?: string;
  /**
   * Tax-exempt shipping amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  exempt?: string | null;
  /**
   * Shipping tax amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  tax?: string | null;
  /**
   * Shipping price after tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  priceAfterTax?: string;
}

export interface AdditionalFee {
  /**
   * Fee ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Fee name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Fee amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  amount?: string;
  /**
   * Entity that added the fee.
   * @internal
   */
  source?: AdditionalFeeSourceWithLiterals;
}

export enum AdditionalFeeSource {
  /** Fee was added by an additional fee service plugin. */
  SERVICE_PLUGIN = 'SERVICE_PLUGIN',
  /** Fee was added to the item by a catalog or custom line item. */
  ITEM = 'ITEM',
  /** Fee was added manually on request. */
  MANUAL = 'MANUAL',
  /** Fee was added by the shipping provider. */
  SHIPPING = 'SHIPPING',
  /** Fee was added by the Wix eCommerce platform. */
  PLATFORM = 'PLATFORM',
}

/** @enumType */
export type AdditionalFeeSourceWithLiterals =
  | AdditionalFeeSource
  | 'SERVICE_PLUGIN'
  | 'ITEM'
  | 'MANUAL'
  | 'SHIPPING'
  | 'PLATFORM';

/** Invoice-level discount. */
export interface Discount extends DiscountDiscountOptionsOneOf {
  /**
   * Fixed discount amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  fixedDiscount?: string;
  /**
   * Percentage discount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:100, maxScale:2 }
   */
  percentageDiscount?: string;
  /**
   * Discount ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Discount name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Type of the discount. */
  discountType?: DiscountTypeWithLiterals;
  /**
   * Calculated discount amount actually applied.
   * @readonly
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  appliedAmount?: string;
}

/** @oneof */
export interface DiscountDiscountOptionsOneOf {
  /**
   * Fixed discount amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  fixedDiscount?: string;
  /**
   * Percentage discount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:100, maxScale:2 }
   */
  percentageDiscount?: string;
}

export enum DiscountType {
  /** Fixed monetary amount. */
  FIXED = 'FIXED',
  /** Percentage-based discount. */
  PERCENTAGE = 'PERCENTAGE',
}

/** @enumType */
export type DiscountTypeWithLiterals = DiscountType | 'FIXED' | 'PERCENTAGE';

/** Deposit configuration for the invoice. The deposit defines the initial payment amount presented at checkout. */
export interface Deposit extends DepositDepositOptionsOneOf {
  /**
   * Fixed deposit amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:1000000000000000, maxScale:4 }
   */
  fixedDeposit?: string;
  /**
   * Deposit as a percentage of the invoice total.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lt:100, maxScale:2 }
   */
  percentageDeposit?: string;
  /** Type of the deposit. */
  depositType?: DepositTypeWithLiterals;
  /**
   * Calculated deposit amount.
   * @readonly
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  total?: string | null;
}

/** @oneof */
export interface DepositDepositOptionsOneOf {
  /**
   * Fixed deposit amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:1000000000000000, maxScale:4 }
   */
  fixedDeposit?: string;
  /**
   * Deposit as a percentage of the invoice total.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lt:100, maxScale:2 }
   */
  percentageDeposit?: string;
}

export enum DepositType {
  /** Fixed monetary amount. */
  FIXED = 'FIXED',
  /** Percentage-based deposit. */
  PERCENTAGE = 'PERCENTAGE',
}

/** @enumType */
export type DepositTypeWithLiterals = DepositType | 'FIXED' | 'PERCENTAGE';

/** Tax information for the invoice. */
export interface TaxInfo {
  /**
   * Breakdown of taxes applied.
   * @maxSize 50
   */
  taxBreakdown?: TaxBreakdown[];
  /**
   * Whether the invoice is tax-exempt.
   * @readonly
   */
  taxExempt?: boolean;
}

/** Breakdown of an individual tax applied to the invoice. */
export interface TaxBreakdown {
  /**
   * Tax name.
   * @maxLength 200
   */
  name?: string;
  /**
   * Tax rate as a decimal. For example, `0.13` for 13%.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string;
  /**
   * Amount subject to this tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  taxableAmount?: string;
  /**
   * Amount exempt from this tax.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  exemptAmount?: string;
  /**
   * Tax amount applied.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  taxAmount?: string;
}

/** Record of a payment made on the invoice. */
export interface Payment
  extends PaymentPaymentOptionsOneOf,
    PaymentReceiptOptionsOneOf {
  /** Regular payment details. */
  regularPayment?: RegularPaymentDetails;
  /** Gift card payment details. */
  giftCardPayment?: GiftCardPaymentDetails;
  /**
   * Wix receipt.
   * @internal
   */
  wixReceipt?: WixReceipt;
  /**
   * External receipt.
   * @internal
   */
  externalReceipt?: ExternalReceipt;
  /**
   * Payment ID.
   * @format GUID
   */
  _id?: string;
  /** Date and time the payment was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  _createdDate?: Date | null;
  /** Date and time the payment was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  _updatedDate?: Date | null;
  /**
   * Payment amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  amount?: string;
  /** Date and time the payment was made. */
  paymentDate?: Date | null;
  /** Type of payment method used. */
  paymentType?: PaymentTypeWithLiterals;
  /** Type of receipt. */
  receiptType?: ReceiptTypeWithLiterals;
  /** Details about cash rounding applied to this payment, when relevant. */
  cashRounding?: CashRounding;
}

/** @oneof */
export interface PaymentPaymentOptionsOneOf {
  /** Regular payment details. */
  regularPayment?: RegularPaymentDetails;
  /** Gift card payment details. */
  giftCardPayment?: GiftCardPaymentDetails;
}

/** @oneof */
export interface PaymentReceiptOptionsOneOf {
  /**
   * Wix receipt.
   * @internal
   */
  wixReceipt?: WixReceipt;
  /**
   * External receipt.
   * @internal
   */
  externalReceipt?: ExternalReceipt;
}

export enum PaymentType {
  /** Standard payment made through Wix Payments, Stripe, or other [supported payment gateways](https://www.wix.com/payments/payment-gateways). Doesn't include gift cards. */
  REGULAR = 'REGULAR',
  /** Payment using a Wix eCommerce gift card. */
  GIFT_CARD = 'GIFT_CARD',
}

/** @enumType */
export type PaymentTypeWithLiterals = PaymentType | 'REGULAR' | 'GIFT_CARD';

export interface RegularPaymentDetails {
  /**
   * Wix Payments order ID. Only populated for online payments.
   * @maxLength 100
   */
  paymentOrderId?: string;
  /**
   * Payment gateway's transaction ID. Only returned when `offlinePayment` is `false`.
   * @maxLength 100
   */
  gatewayTransactionId?: string | null;
  /**
   * Payment method used. Non-exhaustive list of supported values:
   *
   * - CreditCard, Alipay, AstropayCash, AstropayDBT, AstropayMBT, Bitcoin, BitPay, Cash, ConvenienceStore, EPay, Fake, Giropay, IDeal, InPerson, Klarna, MercadoPago, Netpay, NordeaSolo, Offline, PagSeguro, PayEasy, PayPal, Paysafecard, Paysafecash, PointOfSale, Poli, Privat24, Przelewy24, RapidTransfer, Sepa, Skrill, Sofort, Trustly, Neteller, Unionpay, UniPay, Yandex
   * @maxLength 100
   */
  paymentMethod?: string;
  /**
   * Transaction ID in the payment provider's system (e.g., PayPal, Square, Stripe). Not returned for offline payments.
   * @maxLength 100
   */
  providerTransactionId?: string | null;
  /** Whether the payment was made offline. */
  offlinePayment?: boolean;
  /** Payment status. */
  paymentStatus?: PaymentStatusWithLiterals;
  /**
   * Payment provider name (e.g., Wix Payments, PayPal, Square, Stripe).
   * @maxLength 300
   */
  paymentProvider?: string | null;
  /** Credit card details. Only populated when a credit card was used for payment. */
  creditCardDetails?: CreditCardDetails;
}

export enum PaymentStatus {
  /** Payment was voided. */
  VOIDED = 'VOIDED',
  /** Payment was authorized. */
  AUTHORIZED = 'AUTHORIZED',
  /** Payment was partially refunded. */
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  /** Payment was refunded. */
  REFUNDED = 'REFUNDED',
  /** Payment was declined. */
  DECLINED = 'DECLINED',
  /** Payment was canceled. */
  CANCELED = 'CANCELED',
  /** Payment is pending merchant. */
  PENDING_MERCHANT = 'PENDING_MERCHANT',
  /** Payment is pending. */
  PENDING = 'PENDING',
  /** Payment was approved. */
  APPROVED = 'APPROVED',
}

/** @enumType */
export type PaymentStatusWithLiterals =
  | PaymentStatus
  | 'VOIDED'
  | 'AUTHORIZED'
  | 'PARTIALLY_REFUNDED'
  | 'REFUNDED'
  | 'DECLINED'
  | 'CANCELED'
  | 'PENDING_MERCHANT'
  | 'PENDING'
  | 'APPROVED';

export interface CreditCardDetails {
  /**
   * Last 4 digits of the card number.
   * @maxLength 4
   */
  lastFourDigits?: string;
  /**
   * Card brand.
   * @maxLength 100
   */
  brand?: string;
}

export interface GiftCardPaymentDetails {
  /**
   * Gift card payment ID.
   * @minLength 1
   * @maxLength 100
   */
  giftCardPaymentId?: string;
  /**
   * ID of the app that created the gift card.
   * @format GUID
   */
  appId?: string;
}

export enum ReceiptType {
  /** Wix receipt. */
  WIX = 'WIX',
  /** External receipt. */
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type ReceiptTypeWithLiterals = ReceiptType | 'WIX' | 'EXTERNAL';

export interface WixReceipt {
  /**
   * Receipt ID.
   * @format GUID
   */
  receiptId?: string;
  /**
   * Receipt display number.
   * @minLength 1
   * @maxLength 40
   */
  displayNumber?: string | null;
}

export interface ExternalReceipt {
  /**
   * External receipt ID.
   * @maxLength 100
   */
  receiptId?: string | null;
  /**
   * ID of the app providing the receipt.
   * @format GUID
   */
  appId?: string | null;
  /**
   * Receipt display number.
   * @minLength 1
   * @maxLength 40
   */
  displayNumber?: string | null;
}

export interface CashRounding {
  /**
   * The difference between 'amount' and 'unroundedAmount'.
   * A positive value indicates the price was rounded up; a negative value indicates a round-down.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:-1000000000000000, lte:1000000000000000, maxScale:4 }
   */
  roundingAmount?: string;
  /**
   * Payment amount before cash rounding was applied.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  unroundedAmount?: string;
}

/** Financial summary of the invoice. */
export interface Totals {
  /**
   * Subtotal before taxes, discounts, and fees.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  subtotal?: string;
  /**
   * Total discount amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  discount?: string | null;
  /**
   * Total shipping costs.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  shipping?: string | null;
  /**
   * Total tax-exempt amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  exempt?: string | null;
  /**
   * Total tax amount.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  tax?: string | null;
  /**
   * Total amount due.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  total?: string;
  /**
   * Total amount already paid.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  paidAmount?: string;
  /**
   * Outstanding balance.
   * @readonly
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  balance?: string;
}

export interface CustomFields {
  /**
   * Custom fields in the invoice header.
   * @maxSize 4
   */
  headerCustomFields?: CustomField[];
  /**
   * Custom fields in the business details section.
   * @maxSize 5
   */
  businessCustomFields?: CustomField[];
  /**
   * Custom fields in the customer information section.
   * @maxSize 4
   */
  customerCustomFields?: CustomField[];
  /** Footer content. */
  footerCustomField?: FooterContent;
}

export interface CustomField {
  /**
   * Field title.
   * @minLength 1
   * @maxLength 100
   */
  title?: string | null;
  /**
   * Field value.
   * @maxLength 100
   */
  value?: string;
}

export interface FooterContent {
  /** Whether to override the invoice preset's footer content. When `true`, the `content` in this field replaces the preset's default footer on the generated invoice. When `false`, the preset's footer content is used. */
  overridePresetContent?: boolean;
  /** Footer content in rich text format. Used only when `overridePresetContent` is `true`. */
  content?: RichContent;
}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
  /** Data for a checkbox list node. */
  checkboxListData?: CheckboxListData;
  /** Data for a list item node. */
  listItemData?: ListItemNodeData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
  /** Data for a checkbox list node. */
  checkboxListData?: CheckboxListData;
  /** Data for a list item node. */
  listItemData?: ListItemNodeData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
  CARD = 'CARD',
  TOC = 'TOC',
  SMART_BLOCK = 'SMART_BLOCK',
  SMART_BLOCK_CELL = 'SMART_BLOCK_CELL',
  CHECKBOX_LIST = 'CHECKBOX_LIST',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE'
  | 'CARD'
  | 'TOC'
  | 'SMART_BLOCK'
  | 'SMART_BLOCK_CELL'
  | 'CHECKBOX_LIST';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
  /** Node animation. */
  animation?: Animation;
}

/** Background type */
export enum BackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals = BackgroundType | 'COLOR' | 'GRADIENT';

export interface Gradient {
  /** Gradient type. */
  type?: GradientTypeWithLiterals;
  /**
   * Color stops for the gradient.
   * @maxSize 1000
   */
  stops?: Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number | null;
  /**
   * Horizontal center position for radial gradient (0-100).
   * @max 100
   */
  centerX?: number | null;
  /**
   * Vertical center position for radial gradient (0-100).
   * @max 100
   */
  centerY?: number | null;
}

/** Gradient type. */
export enum GradientType {
  /** Linear gradient. */
  LINEAR = 'LINEAR',
  /** Radial gradient. */
  RADIAL = 'RADIAL',
}

/** @enumType */
export type GradientTypeWithLiterals = GradientType | 'LINEAR' | 'RADIAL';

/** A single color stop in the gradient. */
export interface Stop {
  /**
   * Stop color as hex value.
   * @maxLength 19
   */
  color?: string | null;
  /** Stop position (0-1). */
  position?: number | null;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

/** Background styling (color or gradient) */
export interface Background {
  /** Background type. */
  type?: BackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  textColorHover?: string | null;
  /**
   * Deprecated: Use `background` instead.
   * @maxLength 19
   * @deprecated
   */
  backgroundColor?: string | null;
  /**
   * Deprecated: Use `backgroundHover` instead.
   * @maxLength 19
   * @deprecated
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
  /** Background styling (color or gradient). */
  background?: Background;
  /** Background styling for hover state (color or gradient). */
  backgroundHover?: Background;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface Animation {
  /** Animation kind. */
  type?: TypeWithLiterals;
  /** Entrance animation, played once when the node first enters the viewport (VIEW). */
  entrance?: EntranceAnimation;
  /** Looping animation, repeated indefinitely while the node is on the page. When combined with entrance, the loop starts after the entrance finishes (VIEW). */
  loop?: LoopAnimation;
  /**
   * POINTER carries its fields directly on the union member in the JTD wall, hence
   * flat fields here (ts-proto camelCase must equal the JTD property names). A future
   * kind that also needs an `effect` field would contend for this JSON name.
   */
  effect?: PointerEffect;
  /** How quickly the node settles toward the pointer, in milliseconds (POINTER). */
  transitionDuration?: number | null;
}

export enum Type {
  /** Animations that play while the node is in view. */
  VIEW = 'VIEW',
  /** Animations driven by the visitor's mouse position. */
  POINTER = 'POINTER',
}

/** @enumType */
export type TypeWithLiterals = Type | 'VIEW' | 'POINTER';

export interface EntranceAnimation {
  /** The entrance effect to play. */
  effect?: EntranceEffect;
  /** Effect duration in milliseconds. Each effect has its own default. */
  duration?: number | null;
  /** Delay before the effect starts, in milliseconds. */
  delay?: number | null;
}

export interface EntranceEffect {
  /** The entrance effect type. */
  type?: EntranceEffectTypeWithLiterals;
  /**
   * Direction the effect enters from. Allowed values depend on the effect type (e.g. TOP/RIGHT/BOTTOM/LEFT, the diagonals with or without CENTER, corners, HORIZONTAL/VERTICAL, CLOCKWISE/COUNTER_CLOCKWISE).
   * @maxLength 20
   */
  direction?: string | null;
  /**
   * Clip-path shape: ELLIPSE, CIRCLE, RECTANGLE, or DIAMOND (SHAPE).
   * @maxLength 12
   */
  shape?: string | null;
  /**
   * Motion style: GENTLE, MODERATE, or INTENSE. Only the effects that expose one accept it.
   * @maxLength 100
   */
  easing?: string | null;
}

export enum EntranceEffectType {
  /** Fades in from fully transparent to fully opaque. */
  FADE = 'FADE',
  /** Enters along a 3D arc path. */
  ARC = 'ARC',
  /** Transitions from blurred to sharp. */
  BLUR = 'BLUR',
  /** Bounces into place with an elastic curve. */
  BOUNCE = 'BOUNCE',
  /** Shrinks down from a larger size. */
  DROP = 'DROP',
  /** Expands from a point, scaling to full size. */
  EXPAND = 'EXPAND',
  /** Flips into view with a 3D rotation. */
  FLIP = 'FLIP',
  /** Drifts gently into place. */
  FLOAT = 'FLOAT',
  /** Unfolds from an edge as if hinged. */
  FOLD = 'FOLD',
  /** Glides in smoothly from off-screen. */
  GLIDE = 'GLIDE',
  /** Progressively revealed by an expanding clip-path. */
  REVEAL = 'REVEAL',
  /** Appears through an expanding geometric clip-path shape. */
  SHAPE = 'SHAPE',
  /** Revealed through shutter-like strips. */
  SHUTTERS = 'SHUTTERS',
  /** Slides in from one side. */
  SLIDE = 'SLIDE',
  /** Spins into view while scaling up. */
  SPIN = 'SPIN',
  /** Tilts in from the side with 3D rotation. */
  TILT = 'TILT',
  /** Rotates into view around a corner pivot. */
  TURN = 'TURN',
  /** Expands from its horizontal or vertical center. */
  WINK = 'WINK',
}

/** @enumType */
export type EntranceEffectTypeWithLiterals =
  | EntranceEffectType
  | 'FADE'
  | 'ARC'
  | 'BLUR'
  | 'BOUNCE'
  | 'DROP'
  | 'EXPAND'
  | 'FLIP'
  | 'FLOAT'
  | 'FOLD'
  | 'GLIDE'
  | 'REVEAL'
  | 'SHAPE'
  | 'SHUTTERS'
  | 'SLIDE'
  | 'SPIN'
  | 'TILT'
  | 'TURN'
  | 'WINK';

export interface LoopAnimation {
  /** The looping effect to repeat. */
  effect?: LoopEffect;
  /** Duration of a single iteration in milliseconds. Each effect has its own default. */
  duration?: number | null;
  /** Pause between iterations in milliseconds. */
  iterationDelay?: number | null;
}

export interface LoopEffect {
  /** The looping effect type. */
  type?: LoopEffectTypeWithLiterals;
  /**
   * Direction of the effect. Allowed values depend on the effect type (e.g. TOP/RIGHT/BOTTOM/LEFT, LEFT/RIGHT, HORIZONTAL/VERTICAL with or without CENTER, CLOCKWISE/COUNTER_CLOCKWISE).
   * @maxLength 20
   */
  direction?: string | null;
  /**
   * Motion style: GENTLE, MODERATE, or INTENSE. Only the effects that expose one accept it.
   * @maxLength 100
   */
  easing?: string | null;
}

export enum LoopEffectType {
  /** Bounces up and down in place. */
  BOUNCE = 'BOUNCE',
  /** Scales gently in and out along an axis. */
  BREATHE = 'BREATHE',
  /** Drifts toward one side, then re-enters from the opposite side. */
  CROSS = 'CROSS',
  /** Fades out to fully transparent and back in. */
  FLASH = 'FLASH',
  /** Rotates a full turn around its horizontal or vertical axis. */
  FLIP = 'FLIP',
  /** Folds back and forth around a hinged edge. */
  FOLD = 'FOLD',
  /** Wobbles with a jelly-like skew. */
  JELLO = 'JELLO',
  /** Nudges repeatedly toward a direction. */
  POKE = 'POKE',
  /** Scales up and back down rhythmically. */
  PULSE = 'PULSE',
  /** Stretches and squashes like a rubber band. */
  RUBBER = 'RUBBER',
  /** Rotates continuously around its center. */
  SPIN = 'SPIN',
  /** Swings back and forth around an edge pivot. */
  SWING = 'SWING',
  /** Rocks side to side with an accumulating rotation. */
  WIGGLE = 'WIGGLE',
}

/** @enumType */
export type LoopEffectTypeWithLiterals =
  | LoopEffectType
  | 'BOUNCE'
  | 'BREATHE'
  | 'CROSS'
  | 'FLASH'
  | 'FLIP'
  | 'FOLD'
  | 'JELLO'
  | 'POKE'
  | 'PULSE'
  | 'RUBBER'
  | 'SPIN'
  | 'SWING'
  | 'WIGGLE';

export interface PointerEffect {
  /** The mouse effect type. */
  type?: PointerEffectTypeWithLiterals;
  /** Reacts away from the pointer instead of toward it. */
  inverted?: boolean | null;
  /**
   * One field, two value spaces (the effect type says which): the axis the movement is constrained to — HORIZONTAL, VERTICAL, or BOTH (AIRY, SCALE, SKEW, TRACK, TRACK_3D) — or the edge/center axis the node swivels around — TOP, BOTTOM, LEFT, RIGHT, CENTER_HORIZONTAL, or CENTER_VERTICAL (SWIVEL). Only the effects that expose one accept it.
   * @maxLength 20
   */
  axis?: string | null;
  /**
   * Whether the node grows or shrinks as the pointer nears: UP or DOWN (SCALE).
   * @maxLength 8
   */
  direction?: string | null;
  /**
   * Motion style: GENTLE, MODERATE, or INTENSE.
   * @maxLength 100
   */
  easing?: string | null;
}

export enum PointerEffectType {
  /** Drifts loosely after the pointer with a light rotation. */
  AIRY = 'AIRY',
  /** Squashes and stretches toward the pointer. */
  BLOB = 'BLOB',
  /** Shifts after the pointer while blurring. */
  BLUR = 'BLUR',
  /** Scales as the pointer approaches. */
  SCALE = 'SCALE',
  /** Skews toward the pointer. */
  SKEW = 'SKEW',
  /** Rotates around a pivot edge or axis, following the pointer. */
  SWIVEL = 'SWIVEL',
  /** Tilts in 3D perspective toward the pointer. */
  TILT_3D = 'TILT_3D',
  /** Follows the pointer's position. */
  TRACK = 'TRACK',
  /** Follows the pointer with a 3D depth motion. */
  TRACK_3D = 'TRACK_3D',
}

/** @enumType */
export type PointerEffectTypeWithLiterals =
  | PointerEffectType
  | 'AIRY'
  | 'BLOB'
  | 'BLUR'
  | 'SCALE'
  | 'SKEW'
  | 'SWIVEL'
  | 'TILT_3D'
  | 'TRACK'
  | 'TRACK_3D';

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
  /** Node animation. */
  animation?: Animation;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
  /** Visual styling for the divider lines. */
  styles?: DividerDataStyles;
  /** Node animation. */
  animation?: Animation;
}

export enum LineCap {
  /** Square line endings. */
  SQUARE = 'SQUARE',
  /** Rounded line endings. */
  ROUND = 'ROUND',
}

/** @enumType */
export type LineCapWithLiterals = LineCap | 'SQUARE' | 'ROUND';

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface DividerDataStyles {
  /**
   * Divider color as a hexadecimal value. An alpha channel controls opacity.
   * @maxLength 19
   */
  color?: string | null;
  /** Divider line ending shape. */
  lineCap?: LineCapWithLiterals;
  /**
   * Line thicknesses in pixels, ordered from first line to last line.
   * @maxSize 10
   */
  lineThickness?: number[];
  /** Visible dash length for dashed dividers, in pixels. The pattern is snapped to a whole number of dashes so that the first and last dash touch the divider's edges - narrower containers show fewer dashes at the same size. */
  dashLength?: number | null;
  /** Visible gap between dashes or dots, in pixels. */
  dashGap?: number | null;
  /** Gap between divider lines in pixels. */
  lineGap?: number | null;
}

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface Image {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
  /** Node animation. */
  animation?: Animation;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Rendered heading level for SEO/accessibility, overrides the HTML tag when set. */
  renderedLevel?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
  AI_WIDGET = 'AI_WIDGET',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'HTML'
  | 'ADSENSE'
  | 'AI'
  | 'AI_WIDGET';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
  /** Non-destructive crop rectangle, expressed as fractions (0-1) of the original image. When omitted, the full image is shown. */
  crop?: ImageDataCrop;
  /** Optional shape mask applied to the visible crop. Supported values: CIRCLE, OVAL, STAR, PENTAGON, HEXAGON, TRIANGLE, HEART, RHOMBUS, FLUID, WINDOW. */
  cropShape?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface ImageDataCrop {
  /** Left edge of the crop, as a fraction (0-1) of the original image width. */
  x?: number | null;
  /** Top edge of the crop, as a fraction (0-1) of the original image height. */
  y?: number | null;
  /** Visible width of the crop, as a fraction (0-1) of the original image width. */
  width?: number | null;
  /** Visible height of the crop, as a fraction (0-1) of the original image height. */
  height?: number | null;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
  /** Node animation. */
  animation?: Animation;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @maxLength 19
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @maxLength 19
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @maxLength 19
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
  /** Node animation. */
  animation?: Animation;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: Media;
}

export interface Settings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum PollDesignBackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type PollDesignBackgroundTypeWithLiterals =
  | PollDesignBackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface BackgroundGradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @maxLength 19
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @maxLength 19
   */
  lastColor?: string | null;
}

export interface PollDesignBackground
  extends PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @maxLength 19
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
  /** Background type. For each option, include the relevant details. */
  type?: PollDesignBackgroundTypeWithLiterals;
}

/** @oneof */
export interface PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @maxLength 19
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: PollDesignBackground;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: Settings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** Data for a hand-drawn sketch annotation decoration. */
  sketchData?: SketchData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** Data for a hand-drawn sketch annotation decoration. */
  sketchData?: SketchData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
  SKETCH = 'SKETCH',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY'
  | 'SKETCH';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
  /** The kind of entity the mention refers to. Any value is accepted, and it's usually used to mark a mention as a group mention (for example, `group`). When empty, the mention refers to a member. */
  type?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface SketchData {
  /** The sketch annotation variant to draw over the text. */
  variant?: VariantWithLiterals;
  /**
   * Annotation color. Defaults to the theme action color.
   * @maxLength 19
   */
  color?: string | null;
  /** Whether the annotation animates on first paint. Defaults to `true`. */
  animate?: boolean | null;
}

export enum Variant {
  UNDERLINE = 'UNDERLINE',
  BOX = 'BOX',
  CIRCLE = 'CIRCLE',
  HIGHLIGHT = 'HIGHLIGHT',
  STRIKETHROUGH = 'STRIKETHROUGH',
  CROSSED_OFF = 'CROSSED_OFF',
}

/** @enumType */
export type VariantWithLiterals =
  | Variant
  | 'UNDERLINE'
  | 'BOX'
  | 'CIRCLE'
  | 'HIGHLIGHT'
  | 'STRIKETHROUGH'
  | 'CROSSED_OFF';

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
  /** Node animation. */
  animation?: Animation;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @maxLength 19
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: Media;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
  /** Node animation. */
  animation?: Animation;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Table's alternative text. */
  altText?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @maxLength 19
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @maxLength 19
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @maxLength 19
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @maxLength 19
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
  /** Node animation. */
  animation?: Animation;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Deprecated: Use `background` instead.
   * @maxLength 19
   * @deprecated
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: LayoutDataBackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Deprecated: Use `backdrop` instead.
   * @maxLength 19
   * @deprecated
   */
  backdropColor?: string | null;
  /** Backdrop image. */
  backdropImage?: LayoutDataBackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is attached to a document edge (top or bottom). */
  banner?: Banner;
  /** Background styling (color or gradient). */
  background?: LayoutDataBackground;
  /** Backdrop styling (color or gradient). */
  backdrop?: Backdrop;
  /** Node animation. */
  animation?: Animation;
  /** Shape divider at the top edge of the layout. */
  topDivider?: SectionDivider;
  /** Shape divider at the bottom edge of the layout. */
  bottomDivider?: SectionDivider;
}

export enum ImageScalingScaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ImageScalingScalingWithLiterals =
  | ImageScalingScaling
  | 'AUTO'
  | 'CONTAIN'
  | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background styling (color or gradient) */
export interface LayoutDataBackground {
  /** Background type. */
  type?: LayoutDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Background type */
export enum LayoutDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type LayoutDataBackgroundTypeWithLiterals =
  | LayoutDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

export enum BannerPosition {
  /** Attached to the top edge (banner) */
  TOP = 'TOP',
  /** Attached to the bottom edge (footer) */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type BannerPositionWithLiterals = BannerPosition | 'TOP' | 'BOTTOM';

/** How the stacked layers fade relative to the base shape. Named for a bottom divider; top dividers render mirrored, so the visual fade flips. */
export enum LayerEffect {
  /** Shape keeps its height, a solid filler bar pushes it away from the edge */
  CENTER = 'CENTER',
  /** Deeper (more transparent) layers shrink toward the edge inside the original band while a solid filler bar keeps its full height, so the faded copies hug the top of the band */
  FADE_TO_TOP = 'FADE_TO_TOP',
  /** Deeper (more transparent) layers stretch taller into the section, so the faded copies fan out above the base shape and descend to the layout edge */
  FADE_TO_BOTTOM = 'FADE_TO_BOTTOM',
}

/** @enumType */
export type LayerEffectWithLiterals =
  | LayerEffect
  | 'CENTER'
  | 'FADE_TO_TOP'
  | 'FADE_TO_BOTTOM';

/** Divider shape preset */
export enum Shape {
  /** Ellipse shape */
  ELLIPSE = 'ELLIPSE',
  /** Tilt shape */
  TILT = 'TILT',
  /** Liquid shape */
  LIQUID = 'LIQUID',
  /** Left wave shape */
  LEFT_WAVE = 'LEFT_WAVE',
  /** Paint scribble shape */
  PAINT_SCRIBBLE = 'PAINT_SCRIBBLE',
  /** Inverted ellipse shape */
  INVERTED_ELLIPSE = 'INVERTED_ELLIPSE',
  /** Right wave shape */
  RIGHT_WAVE = 'RIGHT_WAVE',
  /** Dunes shape */
  DUNES = 'DUNES',
  /** Waves shape */
  WAVES = 'WAVES',
  /** Triangle shape */
  TRIANGLE = 'TRIANGLE',
  /** Semi-ellipse shape */
  SEMI_ELLIPSE = 'SEMI_ELLIPSE',
  /** Plants shape */
  PLANTS = 'PLANTS',
  /** Layered ellipse shape */
  LAYERED_ELLIPSE = 'LAYERED_ELLIPSE',
  /** Pixels shape */
  PIXELS = 'PIXELS',
  /** Paint shape */
  PAINT = 'PAINT',
  /** Clouds shape */
  CLOUDS = 'CLOUDS',
  /** Optical illusion shape */
  OPTICAL_ILLUSION = 'OPTICAL_ILLUSION',
  /** Stripes shape */
  STRIPES = 'STRIPES',
  /** Blobs shape */
  BLOBS = 'BLOBS',
  /** Semi-circles shape */
  SEMI_CIRCLES = 'SEMI_CIRCLES',
  /** Hill shape */
  HILL = 'HILL',
  /** Brush shape */
  BRUSH = 'BRUSH',
  /** Peaks shape */
  PEAKS = 'PEAKS',
  /** Angled triangle shape */
  ANGLED_TRIANGLE = 'ANGLED_TRIANGLE',
}

/** @enumType */
export type ShapeWithLiterals =
  | Shape
  | 'ELLIPSE'
  | 'TILT'
  | 'LIQUID'
  | 'LEFT_WAVE'
  | 'PAINT_SCRIBBLE'
  | 'INVERTED_ELLIPSE'
  | 'RIGHT_WAVE'
  | 'DUNES'
  | 'WAVES'
  | 'TRIANGLE'
  | 'SEMI_ELLIPSE'
  | 'PLANTS'
  | 'LAYERED_ELLIPSE'
  | 'PIXELS'
  | 'PAINT'
  | 'CLOUDS'
  | 'OPTICAL_ILLUSION'
  | 'STRIPES'
  | 'BLOBS'
  | 'SEMI_CIRCLES'
  | 'HILL'
  | 'BRUSH'
  | 'PEAKS'
  | 'ANGLED_TRIANGLE';

/** Layers effect - stacked semi-transparent copies of the divider shape */
export interface Layers {
  /** Total number of layers including the base shape (1-4). Absent or 1 disables the effect. */
  count?: number | null;
  /** Vertical distance in pixels between the silhouettes of consecutive layers: each successive layer's silhouette is offset from the previous one by this amount. The divider's own `height` is unchanged. Defaults to 20. */
  offsetY?: number | null;
  /** How the stacked layers fade relative to the base shape. */
  layerEffect?: LayerEffectWithLiterals;
  /** Horizontal distance between consecutive layers in pixels. Defaults to 0. Only applies to shapes that support horizontal offset. */
  offsetX?: number | null;
}

/** Backdrop type */
export enum BackdropType {
  /** Solid color backdrop */
  COLOR = 'COLOR',
  /** Gradient backdrop */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackdropTypeWithLiterals = BackdropType | 'COLOR' | 'GRADIENT';

export interface LayoutDataBackgroundImage {
  /** Background image. */
  media?: Media;
  /**
   * Deprecated: use `overlay` instead. Legacy image opacity (0–100) that dimmed the image to reveal the `background`/`backdrop` color behind it.
   * @deprecated
   */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ImageScalingScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
  /** Blur radius in pixels applied to the image layer. `0` (default) leaves the image unblurred; blur is independent of any color overlay and the two stack. */
  blur?: number | null;
  /** Color or gradient drawn on top of the image. When present, this is the authoritative overlay and `opacity` is ignored. Its presence also marks content as authored under the new overlay model (vs. the legacy `opacity`-based dimming on `background`/`backdrop`). */
  overlay?: LayoutDataBackground;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
  /** Position of the banner */
  position?: BannerPositionWithLiterals;
}

/** Backdrop styling (color or gradient) */
export interface Backdrop {
  /** Backdrop type. */
  type?: BackdropTypeWithLiterals;
  /**
   * Backdrop color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Decorative shape divider at a layout edge */
export interface SectionDivider {
  /** Divider shape preset. Determines which customization properties apply. */
  type?: ShapeWithLiterals;
  /**
   * Divider fill color as a hexadecimal value (may include alpha).
   * @maxLength 19
   */
  color?: string | null;
  /** Divider height in pixels. */
  height?: number | null;
  /** Horizontal offset in pixels. */
  offsetX?: number | null;
  /** Whether the shape is flipped horizontally. */
  flipped?: boolean | null;
  /** Layers effect - stacked semi-transparent copies of the divider shape. */
  layers?: Layers;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
  /** Node animation. */
  animation?: Animation;
  /** Link details for shapes that are links. */
  link?: Link;
  /**
   * Alternate text describing the link's purpose for accessibility. Applies only when `link` is set.
   * @maxLength 1000
   */
  altText?: string | null;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface CardData {
  /** Background styling (color or gradient). */
  background?: CardDataBackground;
  /** Background image. */
  backgroundImage?: BackgroundImage;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePositionPosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionPositionWithLiterals =
  | ImagePositionPosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background styling (color or gradient) */
export interface CardDataBackground {
  /** Background type. */
  type?: CardDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Background type */
export enum CardDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type CardDataBackgroundTypeWithLiterals =
  | CardDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

export interface BackgroundImage {
  /** Background image. */
  media?: Media;
  /**
   * Deprecated: use `overlay` instead. Legacy image opacity (0–100) that dimmed the image to reveal the `background` color behind it.
   * @deprecated
   */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionPositionWithLiterals;
  /** Color or gradient drawn on top of the image. When present, this is the authoritative overlay and `opacity` is ignored. Its presence also marks content as authored under the new overlay model (vs. the legacy `opacity`-based dimming on `background`). */
  overlay?: CardDataBackground;
  /** Blur radius in pixels applied to the image layer. `0` (default) leaves the image unblurred; blur is independent of any color overlay and the two stack. */
  blur?: number | null;
}

export interface TocData {
  /** Heading levels included in the table of contents. Default: [1, 2, 3, 4, 5, 6]. */
  includedHeadings?: number[];
  /** List style. Default: PLAIN. */
  listStyle?: ListStyleWithLiterals;
  /** Optional override for the font size in pixels. */
  fontSize?: number | null;
  /** Optional override for the vertical spacing between items in pixels. */
  itemSpacing?: number | null;
  /**
   * Optional override for the text color.
   * @maxLength 19
   */
  color?: string | null;
  /** Indentation style. Default: NESTED. */
  indentation?: IndentationWithLiterals;
  /** Node animation. */
  animation?: Animation;
}

/** List style. */
export enum ListStyle {
  /** No markers (default) */
  PLAIN = 'PLAIN',
  /** Numbered list */
  NUMBERED = 'NUMBERED',
  /** Alphabetic letters */
  LETTERS = 'LETTERS',
  /** Roman numerals */
  ROMAN = 'ROMAN',
  /** Bulleted list */
  BULLETED = 'BULLETED',
  /** Alphabetical index */
  ALPHABETICAL_INDEX = 'ALPHABETICAL_INDEX',
  /** Alphabetical index (compact top-row only) */
  ALPHABETICAL_INDEX_COMPACT = 'ALPHABETICAL_INDEX_COMPACT',
}

/** @enumType */
export type ListStyleWithLiterals =
  | ListStyle
  | 'PLAIN'
  | 'NUMBERED'
  | 'LETTERS'
  | 'ROMAN'
  | 'BULLETED'
  | 'ALPHABETICAL_INDEX'
  | 'ALPHABETICAL_INDEX_COMPACT';

/** Indentation style. */
export enum Indentation {
  /** Sub-headings indented under parents (default) */
  NESTED = 'NESTED',
  /** All items at the same level */
  FLAT = 'FLAT',
}

/** @enumType */
export type IndentationWithLiterals = Indentation | 'NESTED' | 'FLAT';

/** Data for a smart block node. */
export interface SmartBlockData {
  /** The type of the smart block. */
  type?: SmartBlockDataTypeWithLiterals;
  /** Layout orientation. HORIZONTAL or VERTICAL. Optional for variants with fixed orientation. */
  orientation?: string | null;
  /** Column size controlling cells per row. */
  columnSize?: ColumnSizeWithLiterals;
  /**
   * Border color (for SOLID_JOINED_BOXES variant).
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Border width in pixels (for SOLID_JOINED_BOXES variant). */
  borderWidth?: number | null;
  /** Border radius in pixels (for SOLID_JOINED_BOXES variant). */
  borderRadius?: number | null;
  /** Node animation. */
  animation?: Animation;
}

/** Layout type of the smart block */
export enum SmartBlockDataType {
  /** Grid-based layouts with solid box items containing title, body, and icon/image. */
  SOLID_BOXES = 'SOLID_BOXES',
  /** Numbered boxes. */
  NUMBERED_BOXES = 'NUMBERED_BOXES',
  /** Statistics display with large numbers/values. */
  STATS = 'STATS',
  /** Statistics with circular visual elements. */
  CIRCLE_STATS = 'CIRCLE_STATS',
  /** Staggered/zigzag grid layout with alternating box positions. */
  SOLID_BOXES_ALTERNATING = 'SOLID_BOXES_ALTERNATING',
  /** Grid layout with boxes visually joined (no gaps, shared container border). */
  SOLID_JOINED_BOXES = 'SOLID_JOINED_BOXES',
  /** Transparent cells with only a left side line. */
  SIDE_LINE_TEXT = 'SIDE_LINE_TEXT',
  /** Transparent cells with only a top line. */
  TOP_LINE_TEXT = 'TOP_LINE_TEXT',
  /** Outlined boxes with a numbered/icon circle at the top. */
  OUTLINE_BOXES_WITH_TOP_CIRCLE = 'OUTLINE_BOXES_WITH_TOP_CIRCLE',
  /** Large icon bullets with text content. */
  BIG_BULLETS = 'BIG_BULLETS',
  /** Small dot bullets with text content. */
  SMALL_BULLETS = 'SMALL_BULLETS',
  /** Arrow icon bullets with text content. */
  ARROW_BULLETS = 'ARROW_BULLETS',
  /** Process steps with numbered/icon labels above a horizontal line. */
  PROCESS_STEPS = 'PROCESS_STEPS',
  /** Statistics with bar visual elements. */
  BAR_STATS = 'BAR_STATS',
  /** Timeline layout with numbered chips on a connecting line; cells alternate around the line. */
  TIMELINE = 'TIMELINE',
  /** Timeline layout with plain dot indicators; no numbers or shapes; cells alternate around the line. */
  MINIMAL_TIMELINE = 'MINIMAL_TIMELINE',
  /** Numbered pill-shaped labels (stadium chips) with text content; supports HORIZONTAL (pill on top) and VERTICAL (pill on left) orientations. */
  PILLS = 'PILLS',
  /** Star rating display with stars and a numeric value per cell. */
  STAR_RATING = 'STAR_RATING',
  /** Outlined boxes with decorative quote glyphs at the top-left and bottom-right corners. */
  QUOTE_BOXES = 'QUOTE_BOXES',
  /** Donut/ring with numbered annular-sector segments; cell text labels sit outside the ring. */
  CIRCLE = 'CIRCLE',
  /** Hierarchical pyramid where each cell renders as a horizontal slice (apex at top, base at bottom) with the cell number or shape centered inside; text content sits beside (desktop) or below (mobile) the pyramid. */
  PYRAMID = 'PYRAMID',
  /** Cells render a horizontal bar whose width scales with cell index, creating a staircase pattern; bar carries the cell number or a custom shape. */
  STAIRCASE = 'STAIRCASE',
  /** Hierarchical funnel where each cell renders as a horizontal slice (wide at top, narrowing toward the bottom) with the cell number or shape centered inside; text content sits beside (desktop) or below (mobile) the funnel. */
  VERTICAL_FUNNEL = 'VERTICAL_FUNNEL',
}

/** @enumType */
export type SmartBlockDataTypeWithLiterals =
  | SmartBlockDataType
  | 'SOLID_BOXES'
  | 'NUMBERED_BOXES'
  | 'STATS'
  | 'CIRCLE_STATS'
  | 'SOLID_BOXES_ALTERNATING'
  | 'SOLID_JOINED_BOXES'
  | 'SIDE_LINE_TEXT'
  | 'TOP_LINE_TEXT'
  | 'OUTLINE_BOXES_WITH_TOP_CIRCLE'
  | 'BIG_BULLETS'
  | 'SMALL_BULLETS'
  | 'ARROW_BULLETS'
  | 'PROCESS_STEPS'
  | 'BAR_STATS'
  | 'TIMELINE'
  | 'MINIMAL_TIMELINE'
  | 'PILLS'
  | 'STAR_RATING'
  | 'QUOTE_BOXES'
  | 'CIRCLE'
  | 'PYRAMID'
  | 'STAIRCASE'
  | 'VERTICAL_FUNNEL';

/** Column size controlling how many cells appear per row. */
export enum ColumnSize {
  /** Up to 4 cells in a row. */
  SMALL = 'SMALL',
  /** Up to 3 cells in a row (default). */
  MEDIUM = 'MEDIUM',
  /** Up to 2 cells in a row. */
  LARGE = 'LARGE',
  /** 1 cell in a row. */
  EXTRA_LARGE = 'EXTRA_LARGE',
}

/** @enumType */
export type ColumnSizeWithLiterals =
  | ColumnSize
  | 'SMALL'
  | 'MEDIUM'
  | 'LARGE'
  | 'EXTRA_LARGE';

/** Data for a smart block cell node. */
export interface SmartBlockCellData {
  /** Optional label text for the cell (e.g., for stats variants). */
  label?: string | null;
  /** Shape file details. */
  shape?: Media;
  /**
   * Border color of the cell.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /** The type of the parent smart block (must match parent). */
  type?: SmartBlockDataTypeWithLiterals;
  /**
   * Accent color for non-background variants (e.g., line, bullet, label color).
   * @maxLength 19
   */
  accentColor?: string | null;
  /**
   * Background color for background-based variants (SOLID_BOXES, SOLID_BOXES_ALTERNATING, SOLID_JOINED_BOXES).
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Shape fill color as a hexadecimal value.
   * @maxLength 19
   */
  shapeColor?: string | null;
}

export interface CheckboxListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface ListItemNodeData {
  /** Checkbox list item state. Defaults to `false`. */
  checked?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  _id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface PresetProperties {
  /**
   * Invoice preset ID. If not provided, the [default preset](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoice-presets/get-default-invoice-preset) is used.
   * @format GUID
   */
  presetId?: string | null;
  /**
   * Snapshot of the invoice preset at the time the invoice was published.
   * @readonly
   */
  invoicePreset?: InvoicePreset;
}

/** An invoice preset is a reusable template that defines the appearance, content structure, and default settings for invoices. Each site always has at least one preset, and one preset is designated as the default, which is automatically applied when creating invoices. */
export interface InvoicePreset {
  /**
   * Invoice preset ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the invoice preset is updated. To prevent conflicting changes, the current revision must be passed when updating the invoice preset.
   *
   * Ignored when creating an invoice preset.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the invoice preset was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the invoice preset was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Name of the preset.
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /** Display settings that control which sections and fields appear on invoices created with this preset. */
  displaySettings?: DisplaySettings;
  /** Custom fields for adding additional information to invoices, such as header details, business information, customer information, and a footer. */
  customFields?: DomainCustomFields;
  /**
   * Whether this preset is the default preset.
   * @readonly
   */
  defaultPreset?: boolean;
  /** Display value overrides for invoices created with this preset. When set, these values replace the corresponding values on the generated invoice document. */
  displayValues?: DisplayValues;
  /**
   * Number of days after invoice creation until the invoice is due. Set to `0` for due on receipt.
   *
   * Default: `30`
   * @max 365
   */
  defaultDaysUntilDue?: number | null;
}

/** Settings that control which sections and fields appear on invoices created with this preset. */
export interface DisplaySettings {
  /** Options for displaying business information on the invoice. */
  businessInfo?: BusinessDisplayOptions;
  /** Options for displaying customer information on the invoice. */
  customerInfo?: CustomerDisplayOptions;
  /** Options for displaying line item details on the invoice. */
  itemsInfo?: ItemsDisplayOptions;
  /** Options for displaying totals and tax information on the invoice. */
  totalsInfo?: TotalsDisplayOptions;
  /** Options for displaying payment information on the invoice. */
  paymentsInfo?: PaymentsDisplayOptions;
}

/** Options for displaying business details on the invoice. */
export interface BusinessDisplayOptions {
  /** Whether to display the business email on the invoice. */
  displayEmail?: boolean;
  /** Whether to display the business phone number on the invoice. */
  displayPhoneNumber?: boolean;
  /** Whether to display the business address on the invoice. */
  displayAddress?: boolean;
  /** Whether to display the business company ID on the invoice. */
  displayCompanyId?: boolean;
}

/** Options for displaying customer details on the invoice. */
export interface CustomerDisplayOptions {
  /** Whether to display the customer email on the invoice. */
  displayEmail?: boolean;
  /** Whether to display the customer phone number on the invoice. */
  displayPhoneNumber?: boolean;
  /** Whether to display the customer shipping address on the invoice. */
  displayShippingAddress?: boolean;
  /** Whether to display the customer billing address on the invoice. */
  displayBillingAddress?: boolean;
  /** Whether to display the customer VAT ID on the invoice. */
  displayVatId?: boolean;
  /** Whether to display the customer company name on the invoice. */
  displayCompanyName?: boolean;
}

/** Options for displaying line item details on the invoice. */
export interface ItemsDisplayOptions {
  /** Whether to display the item description on the invoice. */
  displayDescription?: boolean;
  /** Whether to display tax information on the invoice. */
  displayTax?: boolean;
}

/** Options for displaying totals and tax details on the invoice. */
export interface TotalsDisplayOptions {
  /** Whether to display a breakdown of taxes applied. */
  displayTaxBreakdown?: boolean;
  /** Whether to display the item subtotal for each tax rate in the breakdown. */
  displayItemSubtotalPerTaxBreakdown?: boolean;
}

/** Options for displaying payment details on the invoice. */
export interface PaymentsDisplayOptions {
  /** Whether to display payment information on the invoice. */
  displayPaymentsInfo?: boolean;
}

/** Custom fields that appear on invoices created with this preset. */
export interface DomainCustomFields {
  /**
   * Custom fields displayed in the header section of the invoice.
   * @maxSize 4
   */
  headerCustomFields?: DomainCustomField[];
  /**
   * Custom fields displayed in the business information section of the invoice.
   * @maxSize 5
   */
  businessCustomFields?: DomainCustomField[];
  /**
   * Custom fields displayed in the customer information section of the invoice.
   * @maxSize 4
   */
  customerCustomFields?: DomainCustomField[];
  /** Footer content displayed at the bottom of the invoice. Supports text formatting and images. Individual invoices can override this content using `customFields.footerCustomField.overridePresetContent`. */
  footerCustomField?: RichContent;
  /**
   * Placeholders for header fields that appear as blank titled fields on the invoice for the recipient to fill in.
   * @maxSize 4
   */
  headerPlaceholders?: CustomFieldPlaceholder[];
  /**
   * Placeholders for customer fields that appear as blank titled fields on the invoice for the recipient to fill in.
   * @maxSize 4
   */
  customerPlaceholders?: CustomFieldPlaceholder[];
}

/** A custom field with a title and value that appears on the invoice. */
export interface DomainCustomField {
  /**
   * Title of the custom field. If omitted, only the value is displayed on the invoice.
   * @minLength 1
   * @maxLength 100
   */
  title?: string | null;
  /**
   * Value of the custom field.
   * @maxLength 100
   */
  value?: string;
}

/** A placeholder that defines a titled blank field on the invoice for the recipient to fill in. */
export interface CustomFieldPlaceholder {
  /**
   * Title of the placeholder field.
   * @minLength 1
   * @maxLength 100
   */
  title?: string;
}

/** Display value overrides for invoices created with this preset. */
export interface DisplayValues {
  /**
   * Title to display on the generated invoice document, overriding the invoice's own `title`. If not set, the invoice's `title` is used.
   * @minLength 1
   * @maxLength 20
   */
  titleOverride?: string | null;
}

export interface Attachment {
  /**
   * File name. For example, `invoice.pdf`.
   * @minLength 1
   * @maxLength 100
   */
  fileName?: string;
  /**
   * File URL.
   * @format WEB_URL
   */
  fileUrl?: string;
}

/** PDF document generation details. */
export interface DocumentInfo {
  /** Document generation status. */
  status?: DocumentStatusWithLiterals;
  /**
   * URL to download the generated PDF. Available when `status` is `AVAILABLE`.
   * @format WEB_URL
   */
  downloadUrl?: string | null;
  /**
   * Date and time the document download URL expires.
   * @readonly
   */
  documentExpirationDate?: Date | null;
}

export enum DocumentStatus {
  /** PDF generation is in progress. */
  PROCESSING = 'PROCESSING',
  /** PDF is ready to download. */
  AVAILABLE = 'AVAILABLE',
  /** PDF generation failed. */
  FAILED = 'FAILED',
  /** PDF download URL has expired. */
  EXPIRED = 'EXPIRED',
  /** PDF is out of date because the invoice has been modified after generation. */
  OUTDATED = 'OUTDATED',
}

/** @enumType */
export type DocumentStatusWithLiterals =
  | DocumentStatus
  | 'PROCESSING'
  | 'AVAILABLE'
  | 'FAILED'
  | 'EXPIRED'
  | 'OUTDATED';

export interface Links {
  /** Relative URL path to the invoice. */
  relativePath?: string;
  /**
   * Full URL to the invoice.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Password required to view the invoice when HIPAA compliance is enabled for the site.
   * @maxLength 4
   */
  previewPassword?: string | null;
}

export interface BusinessLocation {
  /**
   * Location ID. Learn more about the [Wix Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
   * @format GUID
   */
  _id?: string;
  /**
   * Location name.
   * @minLength 1
   * @maxLength 500
   * @readonly
   */
  name?: string | null;
}

export interface ActivityInfo {
  /**
   * Contact IDs that the invoice was sent to.
   * @format GUID
   * @maxSize 5
   */
  sentToContactIds?: string[];
  /** Date and time the invoice was last sent. */
  lastSentDate?: Date | null;
  /** Date and time the invoice was last viewed. */
  lastViewedDate?: Date | null;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, typically restricted from site members and visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone with access to the entity, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface InvoiceNumberingProcess {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId?: string;
}

export interface InvoiceDocumentProcess {
  /**
   * Invoice Id.
   * @format GUID
   */
  invoiceId?: string;
  /**
   * Media Document Id.
   * @format GUID
   */
  documentId?: string;
  /**
   * Existing File Id that will be deleted after the document is generated.
   * @maxLength 100
   */
  existingFileId?: string | null;
}

export interface InvoiceDocumentHandled
  extends InvoiceDocumentHandledResultOneOf {
  /** Populated only if invoice document handling has succeeded. */
  succeeded?: Succeeded;
  /** Populated only if invoice document handling has failed. */
  failed?: Failed;
}

/** @oneof */
export interface InvoiceDocumentHandledResultOneOf {
  /** Populated only if invoice document handling has succeeded. */
  succeeded?: Succeeded;
  /** Populated only if invoice document handling has failed. */
  failed?: Failed;
}

export interface Succeeded {
  /**
   * Invoice Id.
   * @format GUID
   */
  invoiceId?: string;
  /**
   * Media file Id.
   * @maxLength 100
   */
  mediaFileId?: string;
  /**
   * Download URL.
   * @maxLength 200
   */
  downloadUrl?: string;
  /** File expiration date */
  expirationDate?: Date | null;
}

export interface Failed {
  /**
   * Invoice Id.
   * @format GUID
   */
  invoiceId?: string;
}

export interface MediaFileStateChanged {
  /**
   * File ID.
   * @minLength 1
   * @maxLength 36
   */
  fileId?: string;
  /**
   * Meta site ID.
   * @format GUID
   */
  metaSiteId?: string;
  fileState?: FileStateWithLiterals;
}

/** Action type. */
export enum FileState {
  /** File was deleted. */
  DELETED = 'DELETED',
}

/** @enumType */
export type FileStateWithLiterals = FileState | 'DELETED';

/** Requests an overdue side-effect backfill for all invoices of a single meta site. */
export interface SideEffectsTriggerRequested {
  /**
   * Meta site ID to run the side effects trigger for.
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Instance ID of the app on that meta site.
   * @format GUID
   */
  instanceId?: string;
}

export interface SiteTaxesMigrationRequested {
  /**
   * The meta site whose legacy invoices taxes should be migrated.
   * @format GUID
   */
  metaSiteId?: string;
}

/** Payload for the tags modified action event. Contains the invoice and the tags that were added or removed. */
export interface TagsModified {
  /** Invoice that was modified. */
  invoice?: Invoice;
  /** Tags that were assigned to the invoice. */
  assignedTags?: Tags;
  /** Tags that were unassigned from the invoice. */
  unassignedTags?: Tags;
}

/** Payload for the invoice sent action event. Contains the invoice and the contact IDs it was sent to. */
export interface InvoiceSent {
  /** Invoice that was sent. */
  invoice?: Invoice;
  /** Contact IDs that the invoice was sent to. */
  contactIds?: string[];
}

export interface CreateInvoiceRequest {
  /** Invoice to create. */
  invoice: Invoice;
}

export interface CreateInvoiceResponse {
  /** Created invoice. */
  invoice?: Invoice;
}

export interface GetInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface GetInvoiceResponse {
  /** Retrieved invoice. */
  invoice?: Invoice;
}

export interface UpdateInvoiceRequest {
  /** Invoice to update, may be partial. */
  invoice: Invoice;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateInvoiceResponse {
  /** Updated invoice. */
  invoice?: Invoice;
}

export interface IllegalActionErrorData {
  /** Error type. */
  errorType?: ErrorTypeWithLiterals;
  /**
   * Error description.
   * @minLength 1
   * @maxLength 100
   */
  description?: string | null;
}

export enum ErrorType {
  /** Invoice status doesn't allow this action. */
  ILLEGAL_INVOICE_STATE = 'ILLEGAL_INVOICE_STATE',
  /** Action is limited to the app that created the invoice. */
  ACTION_LIMITED_FOR_SOURCE_APPLICATION = 'ACTION_LIMITED_FOR_SOURCE_APPLICATION',
  /** Action is limited to Wix eCommerce for order-type invoices. */
  ACTION_LIMITED_FOR_ORDER_INVOICE = 'ACTION_LIMITED_FOR_ORDER_INVOICE',
  /** Action is limited to the Invoices application for migrated invoices. */
  ACTION_LIMITED_FOR_MIGRATED_INVOICE = 'ACTION_LIMITED_FOR_MIGRATED_INVOICE',
  /** Invoice must be converted using Convert Invoice before this action is available. */
  CONVERSION_REQUIRED = 'CONVERSION_REQUIRED',
}

/** @enumType */
export type ErrorTypeWithLiterals =
  | ErrorType
  | 'ILLEGAL_INVOICE_STATE'
  | 'ACTION_LIMITED_FOR_SOURCE_APPLICATION'
  | 'ACTION_LIMITED_FOR_ORDER_INVOICE'
  | 'ACTION_LIMITED_FOR_MIGRATED_INVOICE'
  | 'CONVERSION_REQUIRED';

export interface ConvertInvoiceRequest {
  /** Complete writable invoice payload to use for conversion. Must include the current `id` and `revision`. Omitted writable fields are cleared. */
  invoice?: Invoice;
}

export interface ConvertInvoiceResponse {
  /** Converted invoice. */
  invoice?: Invoice;
}

export interface DeleteInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface DeleteInvoiceResponse {}

export interface QueryInvoicesRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryInvoicesResponse {
  /** Retrieved invoices. */
  invoices?: Invoice[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
  /**
   * Total number of items matching the filter.
   * Available only on the first page of *Search* results, not included in *Query* or *List* results.
   * If the Search results span multiple pages, the value of `total` will exceed the number of items returned on the first page.
   * @internal
   */
  total?: number | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchInvoicesRequest {
  /** Search options. */
  search?: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Aggregations are a way to explore large amounts of data by displaying summaries about various partitions of the data and later allowing to narrow the navigation to a specific partition.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * UTC offset or IANA time zone. Valid values are
   * ISO 8601 UTC offsets, such as +02:00 or -06:00,
   * and IANA time zone IDs, such as Europe/Rome.
   *
   * Affects all filters and aggregations returned values.
   * You may override this behavior in a specific filter by providing
   * timestamps including time zone. For example, `"2023-12-20T10:52:34.795Z"`.
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation. */
  nested?: NestedAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path.
   * @maxLength 200
   */
  fieldPath?: string;
  /**
   * Deprecated. Use `nested` instead.
   * @deprecated Deprecated. Use `nested` instead.
   * @replacedBy kind.nested
   * @targetRemovalDate 2024-03-30
   */
  groupBy?: GroupByAggregation;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not provided. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not provided. */
  to?: number | null;
}

export enum SortType {
  /** Sort by number of matches. */
  COUNT = 'COUNT',
  /** Sort by value of the field alphabetically. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Sort in descending order. */
  DESC = 'DESC',
  /** Sort in ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Exclude missing values from the aggregation results. */
  EXCLUDE = 'EXCLUDE',
  /** Include missing values in the aggregation results. */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Count of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
  /**
   * Sum of values.
   * @internal
   */
  SUM = 'SUM',
  /**
   * Average of values.
   * @internal
   */
  AVG = 'AVG',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values. */
  includeOptions?: IncludeMissingValuesOptions;
  /** Whether to sort by number of matches or value of the field. */
  sortType?: SortTypeWithLiterals;
  /** Whether to sort in ascending or descending order. */
  sortDirection?: SortDirectionWithLiterals;
  /** How many aggregations to return. Can be between 1 and 250. 10 is the default. */
  limit?: number | null;
  /** Whether to include or exclude missing values from the aggregation results. Default: `EXCLUDE`. */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values. */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket. */
  RANGE = 'RANGE',
  /** A single-value metric aggregation. For example, min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets, where during aggregation each entity will be placed in the first bucket its value falls into, based on the provided range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Define the operator for the scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation. */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Yearly interval */
  YEAR = 'YEAR',
  /** Monthly interval */
  MONTH = 'MONTH',
  /** Weekly interval */
  WEEK = 'WEEK',
  /** Daily interval */
  DAY = 'DAY',
  /** Hourly interval */
  HOUR = 'HOUR',
  /** Minute interval */
  MINUTE = 'MINUTE',
  /** Second interval */
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket. */
  RANGE = 'RANGE',
  /** A single-value metric aggregation. For example, min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.) */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Multi-level aggregation, where each next aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation expressed through a list of aggregation where each next aggregation is nested within previous one. */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each next aggregation is nested within previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface GroupByAggregation extends GroupByAggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Field to aggregate by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface GroupByAggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
}

export interface SearchDetails {
  /** Defines how separate search terms in `expression` are combined. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If empty - will search in all searchable fields. Use dot notation to specify json path.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm). */
  fuzzy?: boolean;
}

export enum Mode {
  /** Any of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchInvoicesResponse {
  /** Retrieved invoices. */
  invoices?: Invoice[];
  /** Paging metadata. */
  pagingMetadata?: PagingMetadataV2;
  /** Response aggregation data. */
  aggregationData?: AggregationData;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface AggregationData {
  /**
   * key = aggregation name (as derived from search request).
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * User-defined name of aggregation, matches the one provided in request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that matches result. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, matches the one provided in request.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of value aggregations.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in ISO 8601 format.
   * @maxLength 100
   */
  value?: string;
  /** Count of documents in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form.
 * Aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregations.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * User-defined name of aggregation as derived from search request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that must match provided kind as derived from search request. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by as derived from search request.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface SendInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
  /**
   * List of contact IDs to send the invoice to. If not provided, the invoice is sent to the customer contact.
   * @format GUID
   * @maxSize 5
   */
  contactIds?: string[];
  /**
   * Optional message included with the sent invoice.
   * @maxLength 2500
   */
  messageContent?: string | null;
}

export interface SendInvoiceResponse {
  /** Sent invoice. */
  invoice?: Invoice;
  /** Results for each contact send attempt. */
  sendResults?: SendResult[];
}

export interface SendResult {
  /**
   * Contact ID the send was attempted for.
   * @format GUID
   */
  contactId?: string;
  /** Whether the send was successful. */
  success?: boolean;
  /** Error type if the send failed. */
  errorType?: SendErrorTypeWithLiterals;
}

export enum SendErrorType {
  /** Couldn't find the contact. */
  CUSTOMER_CONTACT_NOT_FOUND = 'CUSTOMER_CONTACT_NOT_FOUND',
  /** Contact doesn't have an email address. */
  CONTACT_EMAIL_MISSING = 'CONTACT_EMAIL_MISSING',
  /** Unexpected error. */
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

/** @enumType */
export type SendErrorTypeWithLiterals =
  | SendErrorType
  | 'CUSTOMER_CONTACT_NOT_FOUND'
  | 'CONTACT_EMAIL_MISSING'
  | 'INTERNAL_ERROR';

export interface PublishInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface PublishInvoiceResponse {
  /** Published invoice. */
  invoice?: Invoice;
}

export interface VoidInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface VoidInvoiceResponse {
  /** Voided invoice. */
  invoice?: Invoice;
}

export interface ArchiveInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface ArchiveInvoiceResponse {
  /** Archived invoice. */
  invoice?: Invoice;
}

export interface UnarchiveInvoiceRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface UnarchiveInvoiceResponse {
  /** Unarchived invoice. */
  invoice?: Invoice;
}

export interface InitiatePaymentRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface InitiatePaymentResponse {
  /**
   * Checkout URL for the customer to make a payment.
   * @format WEB_URL
   */
  url?: string;
  /**
   * Payment request ID.
   * @format GUID
   */
  paymentRequestId?: string;
}

export interface EnableInvoicePaymentsRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface EnableInvoicePaymentsResponse {
  /**
   * Wix eCommerce order ID.
   * @format GUID
   * @readonly
   */
  orderId?: string;
}

export interface GeneratePdfDocumentRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface GeneratePdfDocumentResponse {
  /** Updated invoice. */
  invoice?: Invoice;
}

export interface MarkInvoiceAsViewedRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface MarkInvoiceAsViewedResponse {
  /** Updated invoice. */
  invoice?: Invoice;
}

export interface MarkInvoiceAsSentRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
}

export interface MarkInvoiceAsSentResponse {
  /** Updated invoice. */
  invoice?: Invoice;
}

export interface CalculateInvoiceRequest {
  /** Invoice to calculate. */
  invoice: Invoice;
  /**
   * Correlation ID from a previous calculation. Pass this to optimize incremental recalculations.
   * @format GUID
   */
  correlationId?: string | null;
}

export interface CalculateInvoiceResponse {
  /** Calculated invoice. */
  invoice?: Invoice;
  /** Available shipping options for the invoice. */
  shipmentOptions?: ShipmentOption[];
  /** Errors that occurred during calculation. */
  calculationErrors?: CalculationErrors;
  /**
   * Correlation ID. Pass this in subsequent calculations to optimize incremental recalculations.
   * @format GUID
   */
  correlationId?: string;
}

export interface ShipmentOption {
  /**
   * Shipping carrier ID.
   * @minLength 1
   * @maxLength 100
   */
  carrierId?: string | null;
  /**
   * Shipping method code.
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  /**
   * Shipping method title.
   * @minLength 1
   * @maxLength 250
   */
  title?: string | null;
  /**
   * Shipping price.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  price?: string | null;
  /** Delivery logistics details. */
  logistics?: DeliveryLogisticsOption;
}

export interface DeliveryLogisticsOption {
  /**
   * Expected delivery time as free text.
   * @minLength 1
   * @maxLength 500
   */
  deliveryTime?: string | null;
  /** Expected delivery time window. */
  deliveryTimeSlot?: DeliveryTimeSlot;
  /**
   * Carrier delivery instructions.
   * @minLength 1
   * @maxLength 1000
   */
  instructions?: string | null;
  /** Additional pickup details. */
  pickupDetails?: PickupDetails;
}

export interface CalculationErrors
  extends CalculationErrorsShippingCalculationErrorOneOf {
  /** General shipping calculation error. */
  generalShippingCalculationError?: Details;
  /** Shipping carrier-specific errors. */
  carrierErrors?: CarrierErrors;
  /** Tax calculation error. */
  taxCalculationError?: Details;
}

/** @oneof */
export interface CalculationErrorsShippingCalculationErrorOneOf {
  /** General shipping calculation error. */
  generalShippingCalculationError?: Details;
  /** Shipping carrier-specific errors. */
  carrierErrors?: CarrierErrors;
}

export interface Details extends DetailsKindOneOf {
  applicationError?: ApplicationError;
  validationError?: ValidationError;
  systemError?: SystemError;
  /**
   * deprecated in API's - to enable migration from rendering arbitrary tracing to rest response
   * @deprecated
   */
  tracing?: Record<string, string>;
}

/** @oneof */
export interface DetailsKindOneOf {
  applicationError?: ApplicationError;
  validationError?: ValidationError;
  systemError?: SystemError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

/**
 * example result:
 * {
 * "fieldViolations": [
 * {
 * "field": "fieldA",
 * "description": "invalid music note. supported notes: [do,re,mi,fa,sol,la,ti]",
 * "violatedRule": "OTHER",
 * "ruleName": "INVALID_NOTE",
 * "data": {
 * "value": "FI"
 * }
 * },
 * {
 * "field": "fieldB",
 * "description": "field value out of range. supported range: [0-20]",
 * "violatedRule": "MAX",
 * "data": {
 * "threshold": 20
 * }
 * },
 * {
 * "field": "fieldC",
 * "description": "invalid phone number. provide a valid phone number of size: [7-12], supported characters: [0-9, +, -, (, )]",
 * "violatedRule": "FORMAT",
 * "data": {
 * "type": "PHONE"
 * }
 * }
 * ]
 * }
 */
export interface ValidationError {
  fieldViolations?: FieldViolation[];
}

export enum RuleType {
  VALIDATION = 'VALIDATION',
  OTHER = 'OTHER',
  MAX = 'MAX',
  MIN = 'MIN',
  MAX_LENGTH = 'MAX_LENGTH',
  MIN_LENGTH = 'MIN_LENGTH',
  MAX_SIZE = 'MAX_SIZE',
  MIN_SIZE = 'MIN_SIZE',
  FORMAT = 'FORMAT',
  DECIMAL_LTE = 'DECIMAL_LTE',
  DECIMAL_GTE = 'DECIMAL_GTE',
  DECIMAL_LT = 'DECIMAL_LT',
  DECIMAL_GT = 'DECIMAL_GT',
  DECIMAL_MAX_SCALE = 'DECIMAL_MAX_SCALE',
  INVALID_ENUM_VALUE = 'INVALID_ENUM_VALUE',
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  FIELD_NOT_ALLOWED = 'FIELD_NOT_ALLOWED',
  ONE_OF_ALIGNMENT = 'ONE_OF_ALIGNMENT',
  EXACT_LENGTH = 'EXACT_LENGTH',
  EXACT_SIZE = 'EXACT_SIZE',
  REQUIRED_ONE_OF_FIELD = 'REQUIRED_ONE_OF_FIELD',
}

/** @enumType */
export type RuleTypeWithLiterals =
  | RuleType
  | 'VALIDATION'
  | 'OTHER'
  | 'MAX'
  | 'MIN'
  | 'MAX_LENGTH'
  | 'MIN_LENGTH'
  | 'MAX_SIZE'
  | 'MIN_SIZE'
  | 'FORMAT'
  | 'DECIMAL_LTE'
  | 'DECIMAL_GTE'
  | 'DECIMAL_LT'
  | 'DECIMAL_GT'
  | 'DECIMAL_MAX_SCALE'
  | 'INVALID_ENUM_VALUE'
  | 'REQUIRED_FIELD'
  | 'FIELD_NOT_ALLOWED'
  | 'ONE_OF_ALIGNMENT'
  | 'EXACT_LENGTH'
  | 'EXACT_SIZE'
  | 'REQUIRED_ONE_OF_FIELD';

export interface FieldViolation {
  field?: string;
  description?: string;
  violatedRule?: RuleTypeWithLiterals;
  /** applicable when violated_rule=OTHER */
  ruleName?: string | null;
  data?: Record<string, any> | null;
}

export interface SystemError {
  /** Error code. */
  errorCode?: string | null;
}

export interface CarrierErrors {
  /**
   * Shipping carrier errors.
   * @maxSize 10
   */
  errors?: CarrierError[];
}

export interface CarrierError {
  /**
   * Carrier ID.
   * @format GUID
   */
  carrierId?: string;
  /** Error details. */
  error?: Details;
}

export interface GetLatestInvoiceNumberRequest {
  /**
   * Invoice number prefix to filter by.
   * @minLength 1
   * @maxLength 10
   */
  prefix?: string | null;
}

export interface GetLatestInvoiceNumberResponse {
  /** Highest invoice number currently in use for the specified prefix. */
  number?: number | null;
}

export interface InternalCountInvoicesRequest {}

export interface InternalCountInvoicesResponse {
  /** Total number of invoices in the current metasite, excluding draft recurring (auto-charge) invoices. */
  count?: number;
  /** Number of draft recurring (auto-charge) invoices in the current metasite. */
  recurringInvoicesCount?: number;
}

export interface GenerateReceiptRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId: string;
  /**
   * Payment ID.
   * @format GUID
   */
  paymentId: string;
}

export interface GenerateReceiptResponse {
  /**
   * Generated receipt ID.
   * @format GUID
   */
  receiptId?: string;
}

export interface BulkCreateInvoicesRequest {
  /**
   * Invoices to create.
   * @minSize 1
   * @maxSize 100
   */
  invoices: Invoice[];
  /** Whether to return the created entities in the response. */
  returnEntity?: boolean;
}

export interface BulkCreateInvoicesResponse {
  /**
   * Results of the bulk create operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkInvoiceResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface BulkInvoiceResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Created invoice. Only returned if `returnEntity` was set to `true` in the request. */
  item?: Invoice;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateInvoicesRequest {
  /**
   * Invoices to update.
   * @minSize 1
   * @maxSize 100
   */
  invoices: MaskedInvoice[];
  /** Whether to return the updated entities in the response. */
  returnEntity?: boolean;
}

export interface MaskedInvoice {
  /** Invoice to update, may be partial. */
  invoice?: Invoice;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   */
  fieldMask?: string[];
}

export interface BulkUpdateInvoicesResponse {
  /**
   * Results of the bulk update operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateInvoicesResponseBulkInvoiceResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateInvoicesResponseBulkInvoiceResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated invoice. Only returned if `returnEntity` was set to `true` in the request. */
  item?: Invoice;
}

export interface BulkDeleteInvoicesRequest {
  /**
   * IDs of invoices to delete.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  invoiceIds: string[];
}

export interface BulkDeleteInvoicesResponse {
  /**
   * Results of the bulk delete operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteInvoicesResponseBulkInvoiceResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteInvoicesResponseBulkInvoiceResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateInvoiceTagsRequest {
  /**
   * IDs of invoices to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  invoiceIds: string[];
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateInvoiceTagsResponse {
  /**
   * Results of the bulk update tags operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateInvoiceTagsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateInvoiceTagsResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateInvoiceTagsByFilterRequest {
  /** Filter for selecting invoices to update. */
  filter: Record<string, any> | null;
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateInvoiceTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job) to retrieve the status of the asynchronous operation.
   * @format GUID
   */
  jobId?: string;
}

export interface AddPaymentRequest {
  /**
   * Invoice ID.
   * @format GUID
   */
  invoiceId?: string;
  /** Payment to add. */
  payment?: Payment;
  /**
   * Whether to also create a payment transaction in the eCommerce system.
   * Defaults to `true` when not set.
   */
  createPaymentTransaction?: boolean | null;
}

export interface AddPaymentResponse {
  /** Updated invoice. */
  invoice?: Invoice;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateInvoiceApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LINE_ITEM_CATALOG_ITEM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEPOSIT_MUST_BE_LESS_THAN_TOTAL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TOTAL_MUST_BE_GREATER_OR_EQUAL_TO_ZERO';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BALANCE_MUST_BE_GREATER_OR_EQUAL_TO_ZERO';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TOTAL_TOO_LOW_FOR_AUTO_CHARGE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVOICE_ORDER_TYPE_CREATION_RESTRICTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVOICE_MIGRATED_TYPE_CREATION_RESTRICTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVOICE_PRESET_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CUSTOMER_CONTACT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_CALCULATE_TAXES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_CALCULATE_SHIPPING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_CALCULATE_CARRIER_SHIPPING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateInvoiceValidationErrors =
  | {
      ruleName?: 'DUE_DATE_BEFORE_ISSUE_DATE';
    }
  | {
      ruleName?: 'INVOICE_DATE_OUT_OF_RANGE';
    }
  | {
      ruleName?: 'TIME_ZONE_INVALID';
    }
  | {
      ruleName?: 'LINE_ITEMS_MUST_EXIST';
    }
  | {
      ruleName?: 'LINE_ITEMS_IDS_MUST_BE_UNIQUE';
    }
  | {
      ruleName?: 'LINE_ITEM_QUANTITY_MUST_BE_INTEGER';
    }
  | {
      ruleName?: 'RICH_CONTENT_TOO_LARGE';
    }
  | {
      ruleName?: 'RICH_CONTENT_INVALID';
    }
  | {
      ruleName?: 'SUBSCRIPTION_MULTIPLE_LINE_ITEMS_NOT_SUPPORTED';
    }
  | {
      ruleName?: 'SUBSCRIPTION_LINE_ITEM_START_DATE_ILLEGAL_FOR_NON_INITIAL_INVOICE';
    }
  | {
      ruleName?: 'SUBSCRIPTION_LINE_ITEM_CURRENT_CYCLE_MUST_BE_INITIAL_FOR_STANDALONE_INVOICE';
    }
  | {
      ruleName?: 'SUBSCRIPTION_NOT_ALLOWED_FOR_REFERENCE_TYPE';
    }
  | {
      ruleName?: 'SUBSCRIPTION_LINE_ITEM_DEPOSIT_NOT_ALLOWED';
    }
  | {
      ruleName?: 'SUBSCRIPTION_MULTIPLE_DISCOUNTS_NOT_SUPPORTED';
    }
  | {
      ruleName?: 'SUBSCRIPTION_BOOKINGS_LINE_ITEM_NOT_SUPPORTED';
    };
/** @docsIgnore */
export type GetInvoiceApplicationErrors = {
  code?: 'SITE_PROPERTIES_FAILED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateInvoiceApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LINE_ITEM_CATALOG_ITEM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEPOSIT_MUST_BE_LESS_THAN_TOTAL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TOTAL_MUST_BE_GREATER_OR_EQUAL_TO_ZERO';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BALANCE_MUST_BE_GREATER_OR_EQUAL_TO_ZERO';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TOTAL_TOO_LOW_FOR_AUTO_CHARGE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    }
  | {
      code?: 'ILLEGAL_FIELD_UPDATE_FOR_PAID_INVOICE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_FIELD_UPDATE_FOR_INVOICE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_FIELD_UPDATE_FOR_PUBLISHED_INVOICE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBSCRIPTION_SETTINGS_IMMUTABLE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVOICE_PRESET_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CUSTOMER_CONTACT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_CALCULATE_TAXES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_CALCULATE_SHIPPING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FAILED_TO_CALCULATE_CARRIER_SHIPPING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateInvoiceValidationErrors =
  | {
      ruleName?: 'DUE_DATE_BEFORE_ISSUE_DATE';
    }
  | {
      ruleName?: 'INVOICE_DATE_OUT_OF_RANGE';
    }
  | {
      ruleName?: 'TIME_ZONE_INVALID';
    }
  | {
      ruleName?: 'LINE_ITEMS_MUST_EXIST';
    }
  | {
      ruleName?: 'LINE_ITEMS_IDS_MUST_BE_UNIQUE';
    }
  | {
      ruleName?: 'LINE_ITEM_QUANTITY_MUST_BE_INTEGER';
    }
  | {
      ruleName?: 'RICH_CONTENT_TOO_LARGE';
    }
  | {
      ruleName?: 'RICH_CONTENT_INVALID';
    }
  | {
      ruleName?: 'SUBSCRIPTION_MULTIPLE_LINE_ITEMS_NOT_SUPPORTED';
    }
  | {
      ruleName?: 'SUBSCRIPTION_LINE_ITEM_START_DATE_ILLEGAL_FOR_NON_INITIAL_INVOICE';
    }
  | {
      ruleName?: 'SUBSCRIPTION_LINE_ITEM_CURRENT_CYCLE_MUST_BE_INITIAL_FOR_STANDALONE_INVOICE';
    }
  | {
      ruleName?: 'SUBSCRIPTION_NOT_ALLOWED_FOR_REFERENCE_TYPE';
    }
  | {
      ruleName?: 'SUBSCRIPTION_LINE_ITEM_DEPOSIT_NOT_ALLOWED';
    }
  | {
      ruleName?: 'SUBSCRIPTION_MULTIPLE_DISCOUNTS_NOT_SUPPORTED';
    }
  | {
      ruleName?: 'SUBSCRIPTION_BOOKINGS_LINE_ITEM_NOT_SUPPORTED';
    };
/** @docsIgnore */
export type DeleteInvoiceApplicationErrors = {
  code?: 'ILLEGAL_ACTION';
  description?: string;
  data?: IllegalActionErrorData;
};
/** @docsIgnore */
export type QueryInvoicesApplicationErrors = {
  code?: 'SITE_PROPERTIES_FAILED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type SendInvoiceApplicationErrors =
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    }
  | {
      code?: 'CUSTOMER_CONTACT_MISSING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SEND_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type PublishInvoiceApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_NOT_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    }
  | {
      code?: 'INVOICE_PRESET_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type VoidInvoiceApplicationErrors =
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    };
/** @docsIgnore */
export type ArchiveInvoiceApplicationErrors = {
  code?: 'ILLEGAL_ACTION';
  description?: string;
  data?: IllegalActionErrorData;
};
/** @docsIgnore */
export type UnarchiveInvoiceApplicationErrors = {
  code?: 'ILLEGAL_ACTION';
  description?: string;
  data?: IllegalActionErrorData;
};
/** @docsIgnore */
export type InitiatePaymentApplicationErrors =
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    }
  | {
      code?: 'INVALID_INVOICE_BALANCE_FOR_ONLINE_PAYMENT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_NOT_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BUSINESS_ADDRESS_MISSING_OR_INVALID_FOR_TAX_CALCULATION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ECOM_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_PAYMENT_REQUEST_PAGE_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type EnableInvoicePaymentsApplicationErrors =
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    }
  | {
      code?: 'BUSINESS_ADDRESS_MISSING_OR_INVALID_FOR_TAX_CALCULATION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type GeneratePdfDocumentApplicationErrors =
  | {
      code?: 'ILLEGAL_ACTION';
      description?: string;
      data?: IllegalActionErrorData;
    }
  | {
      code?: 'INVOICE_PDF_ALREADY_PROCESSING';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CalculateInvoiceApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LINE_ITEM_CATALOG_ITEM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEPOSIT_MUST_BE_LESS_THAN_TOTAL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_INVOICE_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CALCULATION_IN_PROGRESS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkCreateInvoicesApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkCreateInvoicesValidationErrors =
  | {
      ruleName?: 'INVOICE_IDS_MUST_BE_UNIQUE';
    }
  | {
      ruleName?: 'DUE_DATE_BEFORE_ISSUE_DATE';
    }
  | {
      ruleName?: 'INVOICE_DATE_OUT_OF_RANGE';
    }
  | {
      ruleName?: 'TIME_ZONE_INVALID';
    }
  | {
      ruleName?: 'LINE_ITEMS_MUST_EXIST';
    }
  | {
      ruleName?: 'LINE_ITEMS_IDS_MUST_BE_UNIQUE';
    }
  | {
      ruleName?: 'LINE_ITEM_QUANTITY_MUST_BE_INTEGER';
    }
  | {
      ruleName?: 'RICH_CONTENT_TOO_LARGE';
    };
/** @docsIgnore */
export type BulkUpdateInvoicesApplicationErrors =
  | {
      code?: 'SITE_PROPERTIES_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBSCRIPTION_SETTINGS_IMMUTABLE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORDER_TOTAL_EXCEEDS_LIMIT';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateInvoicesValidationErrors =
  | {
      ruleName?: 'DUE_DATE_BEFORE_ISSUE_DATE';
    }
  | {
      ruleName?: 'INVOICE_DATE_OUT_OF_RANGE';
    }
  | {
      ruleName?: 'TIME_ZONE_INVALID';
    }
  | {
      ruleName?: 'LINE_ITEMS_MUST_EXIST';
    }
  | {
      ruleName?: 'LINE_ITEMS_IDS_MUST_BE_UNIQUE';
    }
  | {
      ruleName?: 'RICH_CONTENT_TOO_LARGE';
    }
  | {
      ruleName?: 'RICH_CONTENT_INVALID';
    }
  | {
      ruleName?: 'INVOICE_IDS_MUST_BE_UNIQUE';
    };
/** @docsIgnore */
export type BulkUpdateInvoiceTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateInvoiceTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface InvoiceCreatedEnvelope {
  entity: Invoice;
  metadata: EventMetadata;
}

/**
 * Triggered when an invoice is created.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionId INVOICES.INVOICE_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.invoices.invoices.v4.invoice_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onInvoiceCreated(
  handler: (event: InvoiceCreatedEnvelope) => void | Promise<void>
): void;

export interface InvoiceDeletedEnvelope {
  entity: Invoice;
  metadata: EventMetadata;
}

/**
 * Triggered when an invoice is deleted.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionId INVOICES.INVOICE_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.invoices.invoices.v4.invoice_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onInvoiceDeleted(
  handler: (event: InvoiceDeletedEnvelope) => void | Promise<void>
): void;

export interface InvoiceSentEnvelope {
  data: InvoiceSent;
  metadata: EventMetadata;
}

/**
 * Triggered when an invoice is sent to a customer.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionId INVOICES.INVOICE_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.invoices.invoices.v4.invoice_invoice_sent
 * @slug invoice_sent
 * @documentationMaturity preview
 */
export declare function onInvoiceSent(
  handler: (event: InvoiceSentEnvelope) => void | Promise<void>
): void;

export interface InvoiceTagsModifiedEnvelope {
  data: TagsModified;
  metadata: EventMetadata;
}

/**
 * Triggered when an invoice's tags are modified.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionId INVOICES.INVOICE_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.invoices.invoices.v4.invoice_tags_modified
 * @slug tags_modified
 * @documentationMaturity preview
 */
export declare function onInvoiceTagsModified(
  handler: (event: InvoiceTagsModifiedEnvelope) => void | Promise<void>
): void;

export interface InvoiceUpdatedEnvelope {
  entity: Invoice;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an invoice is updated.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionId INVOICES.INVOICE_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.invoices.invoices.v4.invoice_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onInvoiceUpdated(
  handler: (event: InvoiceUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates an invoice.
 *
 *
 * To create multiple invoices in a single API call, call [Bulk Create Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-create-invoices).
 * @param invoice - Invoice to create.
 * @public
 * @documentationMaturity preview
 * @requiredField invoice
 * @requiredField invoice.currency
 * @requiredField invoice.customerInfo
 * @requiredField invoice.customerInfo.contactId
 * @requiredField invoice.issueDate
 * @requiredField invoice.lineItems
 * @requiredField invoice.lineItems.lineItemOptions.catalogItem.catalogReference
 * @requiredField invoice.lineItems.lineItemOptions.catalogItem.catalogReference.appId
 * @requiredField invoice.lineItems.lineItemOptions.catalogItem.catalogReference.catalogItemId
 * @requiredField invoice.lineItems.lineItemOptions.customItem.name
 * @requiredField invoice.lineItems.lineItemOptions.customItem.price
 * @requiredField invoice.lineItems.lineItemOptions.customItem.quantity
 * @requiredField invoice.lineItems.lineItemType
 * @requiredField invoice.reference
 * @requiredField invoice.reference.referenceOptions
 * @requiredField invoice.reference.referenceOptions.orderReference.orderId
 * @requiredField invoice.shipmentInfo.code
 * @requiredField invoice.shippingInfo.contactDetails
 * @requiredField invoice.shippingInfo.shippingAddress
 * @permissionId INVOICES.INVOICE_CREATE
 * @applicableIdentity APP
 * @returns Created invoice.
 * @fqn wix.invoices.invoices.v4.Invoices.CreateInvoice
 */
export async function createInvoice(
  invoice: NonNullablePaths<
    Invoice,
    | `currency`
    | `customerInfo`
    | `customerInfo.contactId`
    | `issueDate`
    | `lineItems`
    | `lineItems.${number}.catalogItem.catalogReference`
    | `lineItems.${number}.catalogItem.catalogReference.appId`
    | `lineItems.${number}.catalogItem.catalogReference.catalogItemId`
    | `lineItems.${number}.customItem.name`
    | `lineItems.${number}.customItem.price`
    | `lineItems.${number}.customItem.quantity`
    | `lineItems.${number}.lineItemType`
    | `reference`
    | `reference.orderReference.orderId`
    | `reference.referenceType`
    | `shipmentInfo.code`
    | `shipmentInfo.price`
    | `shippingInfo.contactDetails`
    | `shippingInfo.contactDetails.email`
    | `shippingInfo.shippingAddress`,
    6
  >
): Promise<
  NonNullablePaths<
    Invoice,
    | `status`
    | `statusQualifiers`
    | `availableActions`
    | `sourceReference.appId`
    | `reference.standaloneReference.limitActions`
    | `reference.orderReference.orderId`
    | `reference.migratedReference.limitActions`
    | `reference.autoChargeReference.limitActions`
    | `reference.referenceType`
    | `regionalProperties.locale.languageCode`
    | `regionalProperties.language`
    | `numbering.displayNumber`
    | `currency`
    | `customerInfo.contactDetails.vatId._id`
    | `customerInfo.contactDetails.vatId.type`
    | `customerInfo.billingAddress.streetAddress.number`
    | `customerInfo.billingAddress.streetAddress.name`
    | `lineItems`
    | `lineItems.${number}.customItem.quantity`
    | `lineItems.${number}.customItem.price`
    | `lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `lineItems.${number}.customItem.catalogReference.appId`
    | `lineItems.${number}.catalogItem.quantity`
    | `lineItems.${number}.catalogItem.itemType.preset`
    | `lineItems.${number}.catalogItem.itemType.custom`
    | `lineItems.${number}.lineItemType`
    | `shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `shipmentInfo.taxInfo.taxBreakdown`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `shipmentInfo.totals.priceBeforeTax`
    | `shipmentInfo.totals.priceAfterTax`
    | `additionalFees`
    | `additionalFees.${number}.name`
    | `additionalFees.${number}.amount`
    | `discounts`
    | `discounts.${number}.fixedDiscount`
    | `discounts.${number}.percentageDiscount`
    | `discounts.${number}.discountType`
    | `discounts.${number}.appliedAmount`
    | `deposit.fixedDeposit`
    | `deposit.percentageDeposit`
    | `deposit.depositType`
    | `taxInfo.taxBreakdown`
    | `taxInfo.taxBreakdown.${number}.name`
    | `taxInfo.taxBreakdown.${number}.rate`
    | `taxInfo.taxBreakdown.${number}.taxableAmount`
    | `taxInfo.taxBreakdown.${number}.exemptAmount`
    | `taxInfo.taxBreakdown.${number}.taxAmount`
    | `taxInfo.taxExempt`
    | `payments`
    | `payments.${number}.regularPayment.paymentOrderId`
    | `payments.${number}.regularPayment.paymentMethod`
    | `payments.${number}.regularPayment.offlinePayment`
    | `payments.${number}.regularPayment.paymentStatus`
    | `payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `payments.${number}.regularPayment.creditCardDetails.brand`
    | `payments.${number}.giftCardPayment.giftCardPaymentId`
    | `payments.${number}.giftCardPayment.appId`
    | `payments.${number}._id`
    | `payments.${number}.amount`
    | `payments.${number}.paymentType`
    | `payments.${number}.receiptType`
    | `payments.${number}.cashRounding.roundingAmount`
    | `payments.${number}.cashRounding.unroundedAmount`
    | `totals.subtotal`
    | `totals.total`
    | `totals.paidAmount`
    | `totals.balance`
    | `archived`
    | `customFields.headerCustomFields`
    | `customFields.headerCustomFields.${number}.value`
    | `customFields.businessCustomFields`
    | `customFields.customerCustomFields`
    | `customFields.footerCustomField.overridePresetContent`
    | `presetProperties.invoicePreset.name`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `presetProperties.invoicePreset.customFields.headerCustomFields`
    | `presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `presetProperties.invoicePreset.customFields.businessCustomFields`
    | `presetProperties.invoicePreset.customFields.customerCustomFields`
    | `presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `presetProperties.invoicePreset.defaultPreset`
    | `attachments`
    | `attachments.${number}.fileName`
    | `attachments.${number}.fileUrl`
    | `documentInfo.status`
    | `links.relativePath`
    | `businessLocation._id`
    | `activityInfo.sentToContactIds`
    | `hasSubscriptions`
    | `tags.privateTags.tagIds`,
    7
  > & {
    __applicationErrorsType?: CreateInvoiceApplicationErrors;
    __validationErrorsType?: CreateInvoiceValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ invoice: invoice }, [
      'invoice.customFields.footerCustomField.content',
      'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
    ]),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'invoice.customerInfo.billingAddress' },
          { path: 'invoice.shippingInfo.shippingAddress' },
          { path: 'invoice.businessDetails.address' },
          { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.createInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )?.invoice!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoice: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoice']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves an invoice.
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_READ
 * @applicableIdentity APP
 * @returns Retrieved invoice.
 * @fqn wix.invoices.invoices.v4.Invoices.GetInvoice
 */
export async function getInvoice(invoiceId: string): Promise<
  NonNullablePaths<
    Invoice,
    | `status`
    | `statusQualifiers`
    | `availableActions`
    | `sourceReference.appId`
    | `reference.standaloneReference.limitActions`
    | `reference.orderReference.orderId`
    | `reference.migratedReference.limitActions`
    | `reference.autoChargeReference.limitActions`
    | `reference.referenceType`
    | `regionalProperties.locale.languageCode`
    | `regionalProperties.language`
    | `numbering.displayNumber`
    | `currency`
    | `customerInfo.contactDetails.vatId._id`
    | `customerInfo.contactDetails.vatId.type`
    | `customerInfo.billingAddress.streetAddress.number`
    | `customerInfo.billingAddress.streetAddress.name`
    | `lineItems`
    | `lineItems.${number}.customItem.quantity`
    | `lineItems.${number}.customItem.price`
    | `lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `lineItems.${number}.customItem.catalogReference.appId`
    | `lineItems.${number}.catalogItem.quantity`
    | `lineItems.${number}.catalogItem.itemType.preset`
    | `lineItems.${number}.catalogItem.itemType.custom`
    | `lineItems.${number}.lineItemType`
    | `shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `shipmentInfo.taxInfo.taxBreakdown`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `shipmentInfo.totals.priceBeforeTax`
    | `shipmentInfo.totals.priceAfterTax`
    | `additionalFees`
    | `additionalFees.${number}.name`
    | `additionalFees.${number}.amount`
    | `discounts`
    | `discounts.${number}.fixedDiscount`
    | `discounts.${number}.percentageDiscount`
    | `discounts.${number}.discountType`
    | `discounts.${number}.appliedAmount`
    | `deposit.fixedDeposit`
    | `deposit.percentageDeposit`
    | `deposit.depositType`
    | `taxInfo.taxBreakdown`
    | `taxInfo.taxBreakdown.${number}.name`
    | `taxInfo.taxBreakdown.${number}.rate`
    | `taxInfo.taxBreakdown.${number}.taxableAmount`
    | `taxInfo.taxBreakdown.${number}.exemptAmount`
    | `taxInfo.taxBreakdown.${number}.taxAmount`
    | `taxInfo.taxExempt`
    | `payments`
    | `payments.${number}.regularPayment.paymentOrderId`
    | `payments.${number}.regularPayment.paymentMethod`
    | `payments.${number}.regularPayment.offlinePayment`
    | `payments.${number}.regularPayment.paymentStatus`
    | `payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `payments.${number}.regularPayment.creditCardDetails.brand`
    | `payments.${number}.giftCardPayment.giftCardPaymentId`
    | `payments.${number}.giftCardPayment.appId`
    | `payments.${number}._id`
    | `payments.${number}.amount`
    | `payments.${number}.paymentType`
    | `payments.${number}.receiptType`
    | `payments.${number}.cashRounding.roundingAmount`
    | `payments.${number}.cashRounding.unroundedAmount`
    | `totals.subtotal`
    | `totals.total`
    | `totals.paidAmount`
    | `totals.balance`
    | `archived`
    | `customFields.headerCustomFields`
    | `customFields.headerCustomFields.${number}.value`
    | `customFields.businessCustomFields`
    | `customFields.customerCustomFields`
    | `customFields.footerCustomField.overridePresetContent`
    | `presetProperties.invoicePreset.name`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `presetProperties.invoicePreset.customFields.headerCustomFields`
    | `presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `presetProperties.invoicePreset.customFields.businessCustomFields`
    | `presetProperties.invoicePreset.customFields.customerCustomFields`
    | `presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `presetProperties.invoicePreset.defaultPreset`
    | `attachments`
    | `attachments.${number}.fileName`
    | `attachments.${number}.fileUrl`
    | `documentInfo.status`
    | `links.relativePath`
    | `businessLocation._id`
    | `activityInfo.sentToContactIds`
    | `hasSubscriptions`
    | `tags.privateTags.tagIds`,
    7
  > & {
    __applicationErrorsType?: GetInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.getInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )?.invoice!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an invoice.
 *
 *
 * To update multiple invoices in a single API call, call [Bulk Update Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoices).
 * @param _id - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField invoice
 * @requiredField invoice.lineItems.lineItemOptions.customItem.name
 * @requiredField invoice.revision
 * @permissionId INVOICES.INVOICE_UPDATE
 * @applicableIdentity APP
 * @returns Updated invoice.
 * @fqn wix.invoices.invoices.v4.Invoices.UpdateInvoice
 */
export async function updateInvoice(
  _id: string,
  invoice: NonNullablePaths<
    UpdateInvoice,
    `lineItems.${number}.customItem.name` | `revision`,
    5
  >
): Promise<
  NonNullablePaths<
    Invoice,
    | `status`
    | `statusQualifiers`
    | `availableActions`
    | `sourceReference.appId`
    | `reference.standaloneReference.limitActions`
    | `reference.orderReference.orderId`
    | `reference.migratedReference.limitActions`
    | `reference.autoChargeReference.limitActions`
    | `reference.referenceType`
    | `regionalProperties.locale.languageCode`
    | `regionalProperties.language`
    | `numbering.displayNumber`
    | `currency`
    | `customerInfo.contactDetails.vatId._id`
    | `customerInfo.contactDetails.vatId.type`
    | `customerInfo.billingAddress.streetAddress.number`
    | `customerInfo.billingAddress.streetAddress.name`
    | `lineItems`
    | `lineItems.${number}.customItem.quantity`
    | `lineItems.${number}.customItem.price`
    | `lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `lineItems.${number}.customItem.catalogReference.appId`
    | `lineItems.${number}.catalogItem.quantity`
    | `lineItems.${number}.catalogItem.itemType.preset`
    | `lineItems.${number}.catalogItem.itemType.custom`
    | `lineItems.${number}.lineItemType`
    | `shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `shipmentInfo.taxInfo.taxBreakdown`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `shipmentInfo.totals.priceBeforeTax`
    | `shipmentInfo.totals.priceAfterTax`
    | `additionalFees`
    | `additionalFees.${number}.name`
    | `additionalFees.${number}.amount`
    | `discounts`
    | `discounts.${number}.fixedDiscount`
    | `discounts.${number}.percentageDiscount`
    | `discounts.${number}.discountType`
    | `discounts.${number}.appliedAmount`
    | `deposit.fixedDeposit`
    | `deposit.percentageDeposit`
    | `deposit.depositType`
    | `taxInfo.taxBreakdown`
    | `taxInfo.taxBreakdown.${number}.name`
    | `taxInfo.taxBreakdown.${number}.rate`
    | `taxInfo.taxBreakdown.${number}.taxableAmount`
    | `taxInfo.taxBreakdown.${number}.exemptAmount`
    | `taxInfo.taxBreakdown.${number}.taxAmount`
    | `taxInfo.taxExempt`
    | `payments`
    | `payments.${number}.regularPayment.paymentOrderId`
    | `payments.${number}.regularPayment.paymentMethod`
    | `payments.${number}.regularPayment.offlinePayment`
    | `payments.${number}.regularPayment.paymentStatus`
    | `payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `payments.${number}.regularPayment.creditCardDetails.brand`
    | `payments.${number}.giftCardPayment.giftCardPaymentId`
    | `payments.${number}.giftCardPayment.appId`
    | `payments.${number}._id`
    | `payments.${number}.amount`
    | `payments.${number}.paymentType`
    | `payments.${number}.receiptType`
    | `payments.${number}.cashRounding.roundingAmount`
    | `payments.${number}.cashRounding.unroundedAmount`
    | `totals.subtotal`
    | `totals.total`
    | `totals.paidAmount`
    | `totals.balance`
    | `archived`
    | `customFields.headerCustomFields`
    | `customFields.headerCustomFields.${number}.value`
    | `customFields.businessCustomFields`
    | `customFields.customerCustomFields`
    | `customFields.footerCustomField.overridePresetContent`
    | `presetProperties.invoicePreset.name`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `presetProperties.invoicePreset.customFields.headerCustomFields`
    | `presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `presetProperties.invoicePreset.customFields.businessCustomFields`
    | `presetProperties.invoicePreset.customFields.customerCustomFields`
    | `presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `presetProperties.invoicePreset.defaultPreset`
    | `attachments`
    | `attachments.${number}.fileName`
    | `attachments.${number}.fileUrl`
    | `documentInfo.status`
    | `links.relativePath`
    | `businessLocation._id`
    | `activityInfo.sentToContactIds`
    | `hasSubscriptions`
    | `tags.privateTags.tagIds`,
    7
  > & {
    __applicationErrorsType?: UpdateInvoiceApplicationErrors;
    __validationErrorsType?: UpdateInvoiceValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      { invoice: { ...invoice, id: _id } },
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    ),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'invoice.customerInfo.billingAddress' },
          { path: 'invoice.shippingInfo.shippingAddress' },
          { path: 'invoice.businessDetails.address' },
          { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.updateInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )?.invoice!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { invoice: '$[1]' },
        explicitPathsToArguments: { 'invoice.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'invoice']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateInvoice {
  /**
   * Invoice ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the invoice is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the invoice.
   *
   * Ignored when creating an invoice.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the invoice was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the invoice was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Invoice status.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Additional flags providing context about the invoice's state, independent of its primary status. These flags are system-managed and read-only.
   * @readonly
   * @maxSize 2
   */
  statusQualifiers?: StatusQualifierWithLiterals[];
  /**
   * Actions that can be performed on this invoice based on its current status.
   * @readonly
   * @maxSize 12
   */
  availableActions?: ActionWithLiterals[];
  /**
   * Information about the app that created the invoice.
   * @immutable
   */
  sourceReference?: SourceReference;
  /**
   * Invoice type and Wix eCommerce association. May change when an eligible legacy invoice is converted.
   * @immutable
   */
  reference?: Reference;
  /** Regional properties for the invoice. When not provided, defaults are taken from the [Site Properties API](https://dev.wix.com/docs/api-reference/business-management/site-properties/introduction). */
  regionalProperties?: RegionalProperties;
  /**
   * Invoice numbering. Assigned when the invoice is published.
   * @readonly
   */
  numbering?: Numbering;
  /** Date the invoice was issued. */
  issueDate?: Date | null;
  /** Date when payment is due. Cannot be before `issueDate`. */
  dueDate?: Date | null;
  /**
   * Invoice title.
   * @minLength 1
   * @maxLength 200
   */
  title?: string | null;
  /**
   * Currency code for all monetary amounts on the invoice.
   * Three-letter code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * Set at creation and can't be changed.
   * @format CURRENCY
   * @immutable
   */
  currency?: string;
  /** Customer information including contact details and billing address. */
  customerInfo?: CustomerInfo;
  /** Shipping destination for the invoice, including the recipient's contact details and address. Can exist independently without a shipment. When a shipment is configured, `shipmentInfo` uses this address as the delivery destination unless a pickup address is specified. */
  shippingInfo?: ShippingInfo;
  /** Business details displayed on the invoice, such as company name, address, email, phone, and logo. Automatically populated from the [Site Properties API](https://dev.wix.com/docs/api-reference/business-management/site-properties/introduction) when the invoice is created, if not explicitly provided. These details aren't synced with Site Properties after creation and become permanently fixed once `status` reaches `PARTIALLY_PAID`, `PAID`, or `VOIDED`. */
  businessDetails?: BusinessDetails;
  /**
   * Line items on the invoice.
   * @maxSize 300
   */
  lineItems?: LineItem[];
  /** Delivery and shipping cost details (carrier, method, price, tax) tied to the Wix eCommerce integration. Defaults to using `shippingInfo.shippingAddress` as the delivery destination unless `shipmentInfo.logistics.pickupDetails` is specified. */
  shipmentInfo?: ShipmentInfo;
  /**
   * Additional fees applied to the invoice.
   * @maxSize 20
   */
  additionalFees?: AdditionalFee[];
  /**
   * Invoice-level discounts.
   * @maxSize 20
   */
  discounts?: Discount[];
  /** Deposit payment configuration. */
  deposit?: Deposit;
  /**
   * Tax information.
   * @readonly
   */
  taxInfo?: TaxInfo;
  /**
   * Payment records.
   * @maxSize 100
   * @readonly
   */
  payments?: Payment[];
  /**
   * Financial summary including subtotal, taxes, and total amount.
   * @readonly
   */
  totals?: Totals;
  /**
   * Whether the invoice is archived.
   * @readonly
   */
  archived?: boolean;
  /** Custom fields displayed on the invoice document. */
  customFields?: CustomFields;
  /** Invoice preset applied to this invoice. Determines the appearance and custom fields on the invoice document. If not provided, the [default preset](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoice-presets/set-default-invoice-preset) is used. */
  presetProperties?: PresetProperties;
  /**
   * Files attached to the invoice.
   * @maxSize 5
   */
  attachments?: Attachment[];
  /**
   * PDF document generation status.
   * @readonly
   */
  documentInfo?: DocumentInfo;
  /**
   * Invoice URL.
   * @readonly
   */
  links?: Links;
  /** Business location. */
  businessLocation?: BusinessLocation;
  /**
   * Read-only record of when the invoice was last sent and last viewed.
   * @readonly
   */
  activityInfo?: ActivityInfo;
  /**
   * Whether the invoice contains subscription line items. `true` when at least one line item carries subscription info.
   * @readonly
   */
  hasSubscriptions?: boolean;
  /** Tags for organizing invoices. `publicTags` are visible to anyone with access to the invoice. `privateTags` are restricted to site owners and require additional permissions to access. */
  tags?: Tags;
}

/**
 * Deletes an invoice.
 *
 *
 * Deleting an invoice permanently removes it from the invoice list. Only invoices in `DRAFT` status can be deleted.
 *
 * To delete multiple invoices in a single API call, call [Bulk Delete Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-delete-invoices).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_DELETE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.DeleteInvoice
 */
export async function deleteInvoice(invoiceId: string): Promise<
  void & {
    __applicationErrorsType?: DeleteInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.deleteInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 1,000 invoices, given the provided paging, filtering, and sorting.
 *
 *
 * Query Invoices runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId INVOICES.INVOICE_READ
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.QueryInvoices
 */
export function queryInvoices(): InvoicesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Invoice,
    'CURSOR',
    QueryInvoicesRequest,
    QueryInvoicesResponse
  >({
    func: async (payload: QueryInvoicesRequest) => {
      const reqOpts =
        ambassadorWixInvoicesInvoicesV4Invoice.queryInvoices(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryInvoicesRequest['query']) => {
      const args = [query, {}] as [QueryInvoicesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest(
        { ...args?.[1], query: args?.[0] },
        []
      );
    },
    responseTransformer: ({ data }: HttpResponse<QueryInvoicesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              { path: 'invoices.customerInfo.billingAddress' },
              { path: 'invoices.shippingInfo.shippingAddress' },
              { path: 'invoices.businessDetails.address' },
              { path: 'invoices.shipmentInfo.logistics.pickupDetails.address' },
            ],
          },
        ]),
        [
          'invoices.customFields.footerCustomField.content',
          'invoices.presetProperties.invoicePreset.customFields.footerCustomField',
        ]
      );

      return {
        items: transformedData?.invoices,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface InvoicesQueryResult extends QueryCursorResult {
  items: Invoice[];
  query: InvoicesQueryBuilder;
  next: () => Promise<InvoicesQueryResult>;
  prev: () => Promise<InvoicesQueryResult>;
}

export interface InvoicesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'reference.referenceType'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId'
      | 'archived'
      | 'hasSubscriptions',
    value: any
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'reference.referenceType'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId'
      | 'archived'
      | 'hasSubscriptions',
    value: any
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
    value: any
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
    value: any
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
    value: any
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
    value: any
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
    value: string
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: 'tags.privateTags.tagIds' | 'tags.publicTags.tagIds',
    value: any[]
  ) => InvoicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'tags.privateTags.tagIds' | 'tags.publicTags.tagIds',
    value: any[]
  ) => InvoicesQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'reference.referenceType'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId'
      | 'archived'
      | 'hasSubscriptions',
    value: any
  ) => InvoicesQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'reference.referenceType'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId'
      | 'archived'
      | 'hasSubscriptions'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds',
    value: boolean
  ) => InvoicesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'statusQualifiers'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'reference.referenceType'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId'
      | 'archived'
      | 'hasSubscriptions'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds'
    >
  ) => InvoicesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'statusQualifiers'
      | 'sourceReference.appId'
      | 'sourceReference.externalReferenceId'
      | 'reference.standaloneReference.orderId'
      | 'reference.orderReference.orderId'
      | 'reference.migratedReference.orderId'
      | 'reference.referenceType'
      | 'numbering.number'
      | 'numbering.prefix'
      | 'numbering.suffix'
      | 'numbering.displayNumber'
      | 'issueDate'
      | 'dueDate'
      | 'customerInfo.contactId'
      | 'lineItems.customItem.subscriptionInfo.billingSubscriptionId'
      | 'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId'
      | 'archived'
      | 'hasSubscriptions'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds'
    >
  ) => InvoicesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => InvoicesQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => InvoicesQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<InvoicesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.invoices.invoices.v4.Invoices.QueryInvoices
 * @requiredField query
 */
export async function typedQueryInvoices(query: InvoiceQuery): Promise<
  NonNullablePaths<
    QueryInvoicesResponse,
    | `invoices`
    | `invoices.${number}.status`
    | `invoices.${number}.sourceReference.appId`
    | `invoices.${number}.reference.standaloneReference.limitActions`
    | `invoices.${number}.reference.orderReference.orderId`
    | `invoices.${number}.reference.migratedReference.limitActions`
    | `invoices.${number}.reference.autoChargeReference.limitActions`
    | `invoices.${number}.reference.referenceType`
    | `invoices.${number}.regionalProperties.locale.languageCode`
    | `invoices.${number}.regionalProperties.language`
    | `invoices.${number}.numbering.displayNumber`
    | `invoices.${number}.currency`
    | `invoices.${number}.shipmentInfo.totals.priceBeforeTax`
    | `invoices.${number}.shipmentInfo.totals.priceAfterTax`
    | `invoices.${number}.deposit.fixedDeposit`
    | `invoices.${number}.deposit.percentageDeposit`
    | `invoices.${number}.deposit.depositType`
    | `invoices.${number}.taxInfo.taxExempt`
    | `invoices.${number}.totals.subtotal`
    | `invoices.${number}.totals.total`
    | `invoices.${number}.totals.paidAmount`
    | `invoices.${number}.totals.balance`
    | `invoices.${number}.archived`
    | `invoices.${number}.customFields.footerCustomField.overridePresetContent`
    | `invoices.${number}.presetProperties.invoicePreset.name`
    | `invoices.${number}.presetProperties.invoicePreset.defaultPreset`
    | `invoices.${number}.documentInfo.status`
    | `invoices.${number}.links.relativePath`
    | `invoices.${number}.businessLocation._id`
    | `invoices.${number}.hasSubscriptions`,
    6
  > & {
    __applicationErrorsType?: QueryInvoicesApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query }, []);

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.queryInvoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoices.customerInfo.billingAddress' },
            { path: 'invoices.shippingInfo.shippingAddress' },
            { path: 'invoices.businessDetails.address' },
            { path: 'invoices.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoices.customFields.footerCustomField.content',
        'invoices.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface InvoiceQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
        'lineItems.customItem.subscriptionInfo.billingSubscriptionId',
        'statusQualifiers',
        'tags.privateTags.tagIds',
        'tags.publicTags.tagIds'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'archived',
        'customerInfo.contactId',
        'dueDate',
        'hasSubscriptions',
        'issueDate',
        'numbering.displayNumber',
        'numbering.number',
        'numbering.prefix',
        'numbering.suffix',
        'reference.migratedReference.orderId',
        'reference.orderReference.orderId',
        'reference.referenceType',
        'reference.standaloneReference.orderId',
        'sourceReference.appId',
        'sourceReference.externalReferenceId',
        'status'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Invoice,
  InvoiceQuerySpec
>;
export type InvoiceQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
    /** 
  When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
  a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.

  If multiple filters are provided, they are combined with AND operator.

  Example:
  Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
  and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
  { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] } 
  @internal: undefined,
  @maxSize: 10 
  */
    selectItemsBy?:
      | NonNullable<
          CommonQueryWithEntityContext['sort']
        >[number]['selectItemsBy']
      | null;
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    Invoice,
    InvoiceQuerySpec,
    InvoiceQuery
  >(),
  search: /*#__PURE__*/ createSearchUtils<
    Invoice,
    InvoiceSearchSpec,
    InvoiceSearch
  >(),
};

/**
 * Retrieves a list of up to 100 invoices, given the provided filtering, search expression, sorting, and cursor paging.
 *
 *
 * Unlike [Query Invoices], Search Invoices supports free-text search across fields like `title`, `numbering.displayNumber`, and customer contact details. It also returns aggregation data.
 * @param search - Search options.
 * @public
 * @documentationMaturity preview
 * @requiredField search
 * @permissionId INVOICES.INVOICE_READ
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.SearchInvoices
 */
export async function searchInvoices(
  search: InvoiceSearch
): Promise<
  NonNullablePaths<
    SearchInvoicesResponse,
    | `invoices`
    | `invoices.${number}.status`
    | `invoices.${number}.sourceReference.appId`
    | `invoices.${number}.reference.standaloneReference.limitActions`
    | `invoices.${number}.reference.orderReference.orderId`
    | `invoices.${number}.reference.migratedReference.limitActions`
    | `invoices.${number}.reference.autoChargeReference.limitActions`
    | `invoices.${number}.reference.referenceType`
    | `invoices.${number}.regionalProperties.locale.languageCode`
    | `invoices.${number}.regionalProperties.language`
    | `invoices.${number}.numbering.displayNumber`
    | `invoices.${number}.currency`
    | `invoices.${number}.shipmentInfo.totals.priceBeforeTax`
    | `invoices.${number}.shipmentInfo.totals.priceAfterTax`
    | `invoices.${number}.deposit.fixedDeposit`
    | `invoices.${number}.deposit.percentageDeposit`
    | `invoices.${number}.deposit.depositType`
    | `invoices.${number}.taxInfo.taxExempt`
    | `invoices.${number}.totals.subtotal`
    | `invoices.${number}.totals.total`
    | `invoices.${number}.totals.paidAmount`
    | `invoices.${number}.totals.balance`
    | `invoices.${number}.archived`
    | `invoices.${number}.customFields.footerCustomField.overridePresetContent`
    | `invoices.${number}.presetProperties.invoicePreset.name`
    | `invoices.${number}.presetProperties.invoicePreset.defaultPreset`
    | `invoices.${number}.documentInfo.status`
    | `invoices.${number}.links.relativePath`
    | `invoices.${number}.businessLocation._id`
    | `invoices.${number}.hasSubscriptions`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`
    | `aggregationData.results.${number}.fieldPath`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search }, []);

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.searchInvoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoices.customerInfo.billingAddress' },
            { path: 'invoices.shippingInfo.shippingAddress' },
            { path: 'invoices.businessDetails.address' },
            { path: 'invoices.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoices.customFields.footerCustomField.content',
        'invoices.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface InvoiceSearchSpec extends SearchSpec {
  searchable: [
    'customerInfo.contactDetails.company',
    'customerInfo.contactDetails.email',
    'customerInfo.contactDetails.firstName',
    'customerInfo.contactDetails.fullName',
    'customerInfo.contactDetails.lastName',
    'numbering.displayNumber',
    'title'
  ];
  aggregatable: [
    '_createdDate',
    '_id',
    '_updatedDate',
    'archived',
    'currency',
    'customerInfo.contactId',
    'dueDate',
    'hasSubscriptions',
    'issueDate',
    'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
    'lineItems.customItem.subscriptionInfo.billingSubscriptionId',
    'numbering.displayNumber',
    'numbering.number',
    'numbering.prefix',
    'numbering.suffix',
    'reference.migratedReference.orderId',
    'reference.orderReference.orderId',
    'reference.referenceType',
    'reference.standaloneReference.orderId',
    'sourceReference.appId',
    'sourceReference.externalReferenceId',
    'status',
    'statusQualifiers',
    'tags.privateTags.tagIds',
    'tags.publicTags.tagIds'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: ['$hasAll', '$hasSome'];
      fields: [
        'lineItems.catalogItem.subscriptionInfo.billingSubscriptionId',
        'lineItems.customItem.subscriptionInfo.billingSubscriptionId',
        'statusQualifiers',
        'tags.privateTags.tagIds',
        'tags.publicTags.tagIds'
      ];
      sort: 'BOTH';
    },
    {
      operators: '*';
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'archived',
        'currency',
        'customerInfo.contactId',
        'dueDate',
        'hasSubscriptions',
        'issueDate',
        'numbering.displayNumber',
        'numbering.number',
        'numbering.prefix',
        'numbering.suffix',
        'reference.migratedReference.orderId',
        'reference.orderReference.orderId',
        'reference.referenceType',
        'reference.standaloneReference.orderId',
        'sourceReference.appId',
        'sourceReference.externalReferenceId',
        'status'
      ];
      sort: 'BOTH';
    },
    {
      operators: [];
      fields: ['customerInfo.contactDetails.firstName', 'totals.total'];
      sort: 'BOTH';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  Invoice,
  InvoiceSearchSpec
>;
export type InvoiceSearch = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  List of sort objects.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
    /** 
  When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
  a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.

  If multiple filters are provided, they are combined with AND operator.

  Example:
  Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
  and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
  { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] } 
  @internal: undefined,
  @maxSize: 10 
  */
    selectItemsBy?:
      | NonNullable<
          CommonSearchWithEntityContext['sort']
        >[number]['selectItemsBy']
      | null;
  }[];
  /** 
  Aggregations are a way to explore large amounts of data by displaying summaries about various partitions of the data and later allowing to narrow the navigation to a specific partition. 
  @maxSize: 10 
  */
  aggregations?: {
    /** 
  Value aggregation.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  Range aggregation.  
  */
    range?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['range'];
    /** 
  Scalar aggregation.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  Date histogram aggregation.  
  */
    dateHistogram?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['dateHistogram'];
    /** 
  Nested aggregation.  
  */
    nested?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['nested'];
    /** 
  User-defined name of aggregation, should be unique, will appear in aggregation results. 
  @maxLength: 100 
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation, client must provide matching aggregation field below.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Field to aggregate by, use dot notation to specify json path. 
  @maxLength: 200 
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
    /** 
  Deprecated. Use `nested` instead. 
  @deprecated: Deprecated. Use `nested` instead.,
  @replacedBy: kind.nested,
  @targetRemovalDate: 2024-03-30 
  */
    groupBy?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['groupBy'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Defines how separate search terms in `expression` are combined.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression. 
  @maxLength: 100 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If empty - will search in all searchable fields. Use dot notation to specify json path. 
  @maxLength: 200,
  @maxSize: 20 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm).  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
  /** 
  UTC offset or IANA time zone. Valid values are
  ISO 8601 UTC offsets, such as +02:00 or -06:00,
  and IANA time zone IDs, such as Europe/Rome.

  Affects all filters and aggregations returned values.
  You may override this behavior in a specific filter by providing
  timestamps including time zone. For example, `"2023-12-20T10:52:34.795Z"`. 
  @maxLength: 50 
  */
  timeZone?: CommonSearchWithEntityContext['timeZone'] | null;
};

/**
 * Sends an invoice to one or more contacts.
 *
 *
 * Specify `contactIds` in the request to choose recipients. If `contactIds` isn't provided, the invoice is sent to the customer contact on the invoice.
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_SEND
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.SendInvoice
 */
export async function sendInvoice(
  invoiceId: string,
  options?: SendInvoiceOptions
): Promise<
  NonNullablePaths<
    SendInvoiceResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`
    | `sendResults`
    | `sendResults.${number}.contactId`
    | `sendResults.${number}.success`
    | `sendResults.${number}.errorType`,
    8
  > & {
    __applicationErrorsType?: SendInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    {
      invoiceId: invoiceId,
      contactIds: options?.contactIds,
      messageContent: options?.messageContent,
    },
    []
  );

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.sendInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          invoiceId: '$[0]',
          contactIds: '$[1].contactIds',
          messageContent: '$[1].messageContent',
        },
        singleArgumentUnchanged: false,
      },
      ['invoiceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SendInvoiceOptions {
  /**
   * List of contact IDs to send the invoice to. If not provided, the invoice is sent to the customer contact.
   * @format GUID
   * @maxSize 5
   */
  contactIds?: string[];
  /**
   * Optional message included with the sent invoice.
   * @maxLength 2500
   */
  messageContent?: string | null;
}

/**
 * Publishes an invoice.
 *
 *
 * Transitions the invoice status from `DRAFT` to `PUBLISHED`, finalizing it and assigning an invoice number. For details on status transitions, see [Invoice Lifecycle](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/standalone-invoice-payment-lifecycle).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_PUBLISH
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.PublishInvoice
 */
export async function publishInvoice(invoiceId: string): Promise<
  NonNullablePaths<
    PublishInvoiceResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  > & {
    __applicationErrorsType?: PublishInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.publishInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Voids an invoice.
 *
 *
 * Transitions the invoice status to `VOIDED`, canceling it and making it no longer valid. Any payments made before voiding are preserved for the audit trail. For details on status transitions, see [Invoice Lifecycle](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/standalone-invoice-payment-lifecycle).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_VOID
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.VoidInvoice
 */
export async function voidInvoice(invoiceId: string): Promise<
  NonNullablePaths<
    VoidInvoiceResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  > & {
    __applicationErrorsType?: VoidInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts = ambassadorWixInvoicesInvoicesV4Invoice.voidInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Archives an invoice.
 *
 *
 * Use archiving to filter and organize invoices. Archiving is available for all statuses except `DRAFT`.
 *
 * To remove an invoice from the archive, call [Unarchive Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/unarchive-invoice).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_ARCHIVE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.ArchiveInvoice
 */
export async function archiveInvoice(invoiceId: string): Promise<
  NonNullablePaths<
    ArchiveInvoiceResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  > & {
    __applicationErrorsType?: ArchiveInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.archiveInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Unarchives an invoice.
 *
 *
 * Removes the invoice from the archived state. To archive an invoice, call [Archive Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/archive-invoice).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_UNARCHIVE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.UnarchiveInvoice
 */
export async function unarchiveInvoice(invoiceId: string): Promise<
  NonNullablePaths<
    UnarchiveInvoiceResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  > & {
    __applicationErrorsType?: UnarchiveInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.unarchiveInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Initiates payment for an invoice.
 *
 *
 * Creates a Wix eCommerce order if one doesn't exist, generates a payment request, and returns a checkout URL for the customer.
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_PAY
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.InitiatePayment
 */
export async function initiatePayment(invoiceId: string): Promise<
  NonNullablePaths<InitiatePaymentResponse, `url` | `paymentRequestId`, 2> & {
    __applicationErrorsType?: InitiatePaymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.initiatePayment(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Enables payments for an invoice.
 *
 *
 * Use this to record manual (offline) or MOTO (Mail Order/Telephone Order) payments against an invoice. Creates a Wix eCommerce order if one doesn't exist, which enables payment recording against the invoice. To generate a checkout URL for online payment instead, call [Initiate Payment](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/initiate-payment).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_PAY
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.EnableInvoicePayments
 */
export async function enableInvoicePayments(invoiceId: string): Promise<
  NonNullablePaths<EnableInvoicePaymentsResponse, `orderId`, 2> & {
    __applicationErrorsType?: EnableInvoicePaymentsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.enableInvoicePayments(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Generates a PDF document for an invoice.
 *
 *
 * PDF generation is asynchronous. Poll the invoice using [Get Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/get-invoice) and check `documentInfo.status` until it's `AVAILABLE`, then use `documentInfo.downloadUrl` to access the file.
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_GENERATE_PDF_DOCUMENT
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.GeneratePdfDocument
 */
export async function generatePdfDocument(invoiceId: string): Promise<
  NonNullablePaths<
    GeneratePdfDocumentResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  > & {
    __applicationErrorsType?: GeneratePdfDocumentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.generatePdfDocument(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Marks an invoice as viewed.
 *
 *
 * Adds the `VIEWED` status qualifier to the invoice and records the view timestamp in `activityInfo`. For details, see [Status Qualifiers](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/status-qualifiers).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_MARK_AS_VIEWED
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.MarkInvoiceAsViewed
 */
export async function markInvoiceAsViewed(
  invoiceId: string
): Promise<
  NonNullablePaths<
    MarkInvoiceAsViewedResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.markInvoiceAsViewed(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Marks an invoice as sent.
 *
 *
 * Adds the `SENT` status qualifier to the invoice and records the send timestamp and recipient in `activityInfo`. For details, see [Status Qualifiers](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/status-qualifiers).
 * @param invoiceId - Invoice ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @permissionId INVOICES.INVOICE_MARK_AS_SENT
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.MarkInvoiceAsSent
 */
export async function markInvoiceAsSent(
  invoiceId: string
): Promise<
  NonNullablePaths<
    MarkInvoiceAsSentResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`,
    8
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.markInvoiceAsSent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Calculates totals, taxes, and available shipping options for a standalone invoice. Supports both custom and catalog line items.
 *
 *
 * Use this method before creating or updating an invoice to preview calculated values. Pass the returned `correlationId` in subsequent calculations to optimize incremental recalculations.
 * @param invoice - Invoice to calculate.
 * @public
 * @documentationMaturity preview
 * @requiredField invoice
 * @requiredField invoice.reference
 * @requiredField invoice.reference.referenceOptions
 * @requiredField invoice.reference.referenceType
 * @permissionId INVOICES.INVOICE_CREATE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.CalculateInvoice
 */
export async function calculateInvoice(
  invoice: NonNullablePaths<
    Invoice,
    `reference` | `reference.referenceType`,
    3
  >,
  options?: CalculateInvoiceOptions
): Promise<
  NonNullablePaths<
    CalculateInvoiceResponse,
    | `invoice.status`
    | `invoice.statusQualifiers`
    | `invoice.availableActions`
    | `invoice.sourceReference.appId`
    | `invoice.reference.standaloneReference.limitActions`
    | `invoice.reference.orderReference.orderId`
    | `invoice.reference.migratedReference.limitActions`
    | `invoice.reference.autoChargeReference.limitActions`
    | `invoice.reference.referenceType`
    | `invoice.regionalProperties.locale.languageCode`
    | `invoice.regionalProperties.language`
    | `invoice.numbering.displayNumber`
    | `invoice.currency`
    | `invoice.customerInfo.contactDetails.vatId._id`
    | `invoice.customerInfo.contactDetails.vatId.type`
    | `invoice.customerInfo.billingAddress.streetAddress.number`
    | `invoice.customerInfo.billingAddress.streetAddress.name`
    | `invoice.lineItems`
    | `invoice.lineItems.${number}.customItem.quantity`
    | `invoice.lineItems.${number}.customItem.price`
    | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
    | `invoice.lineItems.${number}.customItem.catalogReference.appId`
    | `invoice.lineItems.${number}.catalogItem.quantity`
    | `invoice.lineItems.${number}.catalogItem.itemType.preset`
    | `invoice.lineItems.${number}.catalogItem.itemType.custom`
    | `invoice.lineItems.${number}.lineItemType`
    | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.shipmentInfo.totals.priceBeforeTax`
    | `invoice.shipmentInfo.totals.priceAfterTax`
    | `invoice.additionalFees`
    | `invoice.additionalFees.${number}.name`
    | `invoice.additionalFees.${number}.amount`
    | `invoice.discounts`
    | `invoice.discounts.${number}.fixedDiscount`
    | `invoice.discounts.${number}.percentageDiscount`
    | `invoice.discounts.${number}.discountType`
    | `invoice.discounts.${number}.appliedAmount`
    | `invoice.deposit.fixedDeposit`
    | `invoice.deposit.percentageDeposit`
    | `invoice.deposit.depositType`
    | `invoice.taxInfo.taxBreakdown`
    | `invoice.taxInfo.taxBreakdown.${number}.name`
    | `invoice.taxInfo.taxBreakdown.${number}.rate`
    | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
    | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
    | `invoice.taxInfo.taxExempt`
    | `invoice.payments`
    | `invoice.payments.${number}.regularPayment.paymentOrderId`
    | `invoice.payments.${number}.regularPayment.paymentMethod`
    | `invoice.payments.${number}.regularPayment.offlinePayment`
    | `invoice.payments.${number}.regularPayment.paymentStatus`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
    | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
    | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
    | `invoice.payments.${number}.giftCardPayment.appId`
    | `invoice.payments.${number}._id`
    | `invoice.payments.${number}.amount`
    | `invoice.payments.${number}.paymentType`
    | `invoice.payments.${number}.receiptType`
    | `invoice.payments.${number}.cashRounding.roundingAmount`
    | `invoice.payments.${number}.cashRounding.unroundedAmount`
    | `invoice.totals.subtotal`
    | `invoice.totals.total`
    | `invoice.totals.paidAmount`
    | `invoice.totals.balance`
    | `invoice.archived`
    | `invoice.customFields.headerCustomFields`
    | `invoice.customFields.headerCustomFields.${number}.value`
    | `invoice.customFields.businessCustomFields`
    | `invoice.customFields.customerCustomFields`
    | `invoice.customFields.footerCustomField.overridePresetContent`
    | `invoice.presetProperties.invoicePreset.name`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
    | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
    | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
    | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
    | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
    | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
    | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
    | `invoice.presetProperties.invoicePreset.defaultPreset`
    | `invoice.attachments`
    | `invoice.attachments.${number}.fileName`
    | `invoice.attachments.${number}.fileUrl`
    | `invoice.documentInfo.status`
    | `invoice.links.relativePath`
    | `invoice.businessLocation._id`
    | `invoice.activityInfo.sentToContactIds`
    | `invoice.hasSubscriptions`
    | `invoice.tags.privateTags.tagIds`
    | `shipmentOptions`
    | `shipmentOptions.${number}.logistics.pickupDetails.pickupMethod`
    | `calculationErrors.generalShippingCalculationError.applicationError.code`
    | `calculationErrors.generalShippingCalculationError.applicationError.description`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.field`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.description`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.violatedRule`
    | `calculationErrors.carrierErrors.errors`
    | `calculationErrors.carrierErrors.errors.${number}.carrierId`
    | `correlationId`,
    8
  > & {
    __applicationErrorsType?: CalculateInvoiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      { invoice: invoice, correlationId: options?.correlationId },
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    ),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'invoice.customerInfo.billingAddress' },
          { path: 'invoice.shippingInfo.shippingAddress' },
          { path: 'invoice.businessDetails.address' },
          { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.calculateInvoice(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'invoice.customerInfo.billingAddress' },
            { path: 'invoice.shippingInfo.shippingAddress' },
            { path: 'invoice.businessDetails.address' },
            { path: 'invoice.shipmentInfo.logistics.pickupDetails.address' },
            { path: 'shipmentOptions.logistics.pickupDetails.address' },
          ],
        },
      ]),
      [
        'invoice.customFields.footerCustomField.content',
        'invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          invoice: '$[0]',
          correlationId: '$[1].correlationId',
        },
        singleArgumentUnchanged: false,
      },
      ['invoice', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CalculateInvoiceOptions {
  /**
   * Correlation ID from a previous calculation. Pass this to optimize incremental recalculations.
   * @format GUID
   */
  correlationId?: string | null;
}

/**
 * Retrieves the highest invoice number currently in use.
 *
 *
 * Invoice numbering is managed in the [Invoices Settings API](https://dev.wix.com/docs/rest/business-management/get-paid/invoices/invoices-settings/introduction).
 * @public
 * @documentationMaturity preview
 * @permissionId INVOICES.INVOICE_READ
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.GetLatestInvoiceNumber
 */
export async function getLatestInvoiceNumber(
  options?: GetLatestInvoiceNumberOptions
): Promise<GetLatestInvoiceNumberResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { prefix: options?.prefix },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.getLatestInvoiceNumber(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { prefix: '$[0].prefix' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetLatestInvoiceNumberOptions {
  /**
   * Invoice number prefix to filter by.
   * @minLength 1
   * @maxLength 10
   */
  prefix?: string | null;
}

/**
 * Generates a receipt document for a specific payment on an invoice. Requires the `paymentId` of a confirmed payment on the invoice.
 * @param invoiceId - Invoice ID.
 * @param paymentId - Payment ID.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceId
 * @requiredField paymentId
 * @permissionId INVOICES.INVOICE_GENERATE_RECEIPT
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.GenerateReceipt
 */
export async function generateReceipt(
  invoiceId: string,
  paymentId: string
): Promise<NonNullablePaths<GenerateReceiptResponse, `receiptId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceId: invoiceId, paymentId: paymentId },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.generateReceipt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceId: '$[0]', paymentId: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceId', 'paymentId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates multiple invoices.
 *
 *
 * To create a single invoice, call [Create Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/create-invoice).
 * @param invoices - Invoices to create.
 * @public
 * @documentationMaturity preview
 * @requiredField invoices
 * @requiredField invoices.currency
 * @requiredField invoices.customerInfo
 * @requiredField invoices.customerInfo.contactId
 * @requiredField invoices.issueDate
 * @requiredField invoices.lineItems
 * @requiredField invoices.lineItems.lineItemOptions.catalogItem.catalogReference
 * @requiredField invoices.lineItems.lineItemOptions.catalogItem.catalogReference.appId
 * @requiredField invoices.lineItems.lineItemOptions.catalogItem.catalogReference.catalogItemId
 * @requiredField invoices.lineItems.lineItemOptions.customItem.name
 * @requiredField invoices.lineItems.lineItemOptions.customItem.price
 * @requiredField invoices.lineItems.lineItemOptions.customItem.quantity
 * @requiredField invoices.lineItems.lineItemType
 * @requiredField invoices.reference
 * @requiredField invoices.reference.referenceOptions
 * @requiredField invoices.reference.referenceOptions.orderReference.orderId
 * @requiredField invoices.shipmentInfo.code
 * @requiredField invoices.shippingInfo.contactDetails
 * @requiredField invoices.shippingInfo.shippingAddress
 * @permissionId INVOICES.INVOICE_CREATE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.BulkCreateInvoices
 */
export async function bulkCreateInvoices(
  invoices: NonNullablePaths<
    Invoice,
    | `currency`
    | `customerInfo`
    | `customerInfo.contactId`
    | `issueDate`
    | `lineItems`
    | `lineItems.${number}.catalogItem.catalogReference`
    | `lineItems.${number}.catalogItem.catalogReference.appId`
    | `lineItems.${number}.catalogItem.catalogReference.catalogItemId`
    | `lineItems.${number}.customItem.name`
    | `lineItems.${number}.customItem.price`
    | `lineItems.${number}.customItem.quantity`
    | `lineItems.${number}.lineItemType`
    | `reference`
    | `reference.orderReference.orderId`
    | `reference.referenceType`
    | `shipmentInfo.code`
    | `shipmentInfo.price`
    | `shippingInfo.contactDetails`
    | `shippingInfo.contactDetails.email`
    | `shippingInfo.shippingAddress`,
    6
  >[],
  options?: BulkCreateInvoicesOptions
): Promise<
  NonNullablePaths<
    BulkCreateInvoicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.status`
    | `results.${number}.item.sourceReference.appId`
    | `results.${number}.item.reference.referenceType`
    | `results.${number}.item.regionalProperties.language`
    | `results.${number}.item.numbering.displayNumber`
    | `results.${number}.item.currency`
    | `results.${number}.item.deposit.fixedDeposit`
    | `results.${number}.item.deposit.percentageDeposit`
    | `results.${number}.item.deposit.depositType`
    | `results.${number}.item.taxInfo.taxExempt`
    | `results.${number}.item.totals.subtotal`
    | `results.${number}.item.totals.total`
    | `results.${number}.item.totals.paidAmount`
    | `results.${number}.item.totals.balance`
    | `results.${number}.item.archived`
    | `results.${number}.item.documentInfo.status`
    | `results.${number}.item.links.relativePath`
    | `results.${number}.item.businessLocation._id`
    | `results.${number}.item.hasSubscriptions`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkCreateInvoicesApplicationErrors;
    __validationErrorsType?: BulkCreateInvoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      { invoices: invoices, returnEntity: options?.returnEntity },
      [
        'invoices.customFields.footerCustomField.content',
        'invoices.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    ),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'invoices.customerInfo.billingAddress' },
          { path: 'invoices.shippingInfo.shippingAddress' },
          { path: 'invoices.businessDetails.address' },
          { path: 'invoices.shipmentInfo.logistics.pickupDetails.address' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.bulkCreateInvoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'results.item.customerInfo.billingAddress' },
            { path: 'results.item.shippingInfo.shippingAddress' },
            { path: 'results.item.businessDetails.address' },
            {
              path: 'results.item.shipmentInfo.logistics.pickupDetails.address',
            },
          ],
        },
      ]),
      [
        'results.item.customFields.footerCustomField.content',
        'results.item.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          invoices: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['invoices', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateInvoicesOptions {
  /** Whether to return the created entities in the response. */
  returnEntity?: boolean;
}

/**
 * Updates multiple invoices.
 *
 *
 * To update a single invoice, call [Update Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/update-invoice).
 * @param invoices - Invoices to update.
 * @public
 * @documentationMaturity preview
 * @requiredField invoices
 * @requiredField invoices.invoice
 * @requiredField invoices.invoice._id
 * @requiredField invoices.invoice.lineItems.lineItemOptions.customItem.name
 * @requiredField invoices.invoice.revision
 * @permissionId INVOICES.INVOICE_UPDATE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.BulkUpdateInvoices
 */
export async function bulkUpdateInvoices(
  invoices: NonNullablePaths<
    MaskedInvoice,
    | `invoice`
    | `invoice._id`
    | `invoice.lineItems.${number}.customItem.name`
    | `invoice.revision`,
    6
  >[],
  options?: BulkUpdateInvoicesOptions
): Promise<
  NonNullablePaths<
    BulkUpdateInvoicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.status`
    | `results.${number}.item.sourceReference.appId`
    | `results.${number}.item.reference.referenceType`
    | `results.${number}.item.regionalProperties.language`
    | `results.${number}.item.numbering.displayNumber`
    | `results.${number}.item.currency`
    | `results.${number}.item.deposit.fixedDeposit`
    | `results.${number}.item.deposit.percentageDeposit`
    | `results.${number}.item.deposit.depositType`
    | `results.${number}.item.taxInfo.taxExempt`
    | `results.${number}.item.totals.subtotal`
    | `results.${number}.item.totals.total`
    | `results.${number}.item.totals.paidAmount`
    | `results.${number}.item.totals.balance`
    | `results.${number}.item.archived`
    | `results.${number}.item.documentInfo.status`
    | `results.${number}.item.links.relativePath`
    | `results.${number}.item.businessLocation._id`
    | `results.${number}.item.hasSubscriptions`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateInvoicesApplicationErrors;
    __validationErrorsType?: BulkUpdateInvoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      { invoices: invoices, returnEntity: options?.returnEntity },
      [
        'invoices.invoice.customFields.footerCustomField.content',
        'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    ),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'invoices.invoice.customerInfo.billingAddress' },
          { path: 'invoices.invoice.shippingInfo.shippingAddress' },
          { path: 'invoices.invoice.businessDetails.address' },
          {
            path: 'invoices.invoice.shipmentInfo.logistics.pickupDetails.address',
          },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.bulkUpdateInvoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'results.item.customerInfo.billingAddress' },
            { path: 'results.item.shippingInfo.shippingAddress' },
            { path: 'results.item.businessDetails.address' },
            {
              path: 'results.item.shipmentInfo.logistics.pickupDetails.address',
            },
          ],
        },
      ]),
      [
        'results.item.customFields.footerCustomField.content',
        'results.item.presetProperties.invoicePreset.customFields.footerCustomField',
      ]
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          invoices: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['invoices', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateInvoicesOptions {
  /** Whether to return the updated entities in the response. */
  returnEntity?: boolean;
}

/**
 * Deletes multiple invoices.
 *
 *
 * To delete a single invoice, call [Delete Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/delete-invoice).
 * @param invoiceIds - IDs of invoices to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceIds
 * @permissionId INVOICES.INVOICE_DELETE
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.BulkDeleteInvoices
 */
export async function bulkDeleteInvoices(
  invoiceIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteInvoicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { invoiceIds: invoiceIds },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.bulkDeleteInvoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { invoiceIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['invoiceIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds or removes tags on multiple invoices. Tags are labels used to organize and filter invoices — they can be public (visible to anyone with access to the invoice) or private (restricted to site owners).
 *
 * Unlike [Bulk Update Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoices), which replaces fields entirely, this method lets you specify individual tags to assign or unassign without affecting other existing tags. A tag that appears in both the list to assign and the list to unassign is assigned. Tags that don't exist are ignored.
 *
 * To update tags on all invoices matching a filter, call [Bulk Update Invoice Tags By Filter](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoice-tags-by-filter).
 * @param invoiceIds - IDs of invoices to update tags for.
 * @public
 * @documentationMaturity preview
 * @requiredField invoiceIds
 * @permissionId INVOICES.INVOICE_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.BulkUpdateInvoiceTags
 */
export async function bulkUpdateInvoiceTags(
  invoiceIds: string[],
  options?: BulkUpdateInvoiceTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateInvoiceTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateInvoiceTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    {
      invoiceIds: invoiceIds,
      assignTags: options?.assignTags,
      unassignTags: options?.unassignTags,
    },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.bulkUpdateInvoiceTags(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          invoiceIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['invoiceIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateInvoiceTagsOptions {
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

/**
 * Asynchronously updates tags on multiple invoices that match the provided filter criteria.
 *
 *
 * To check the status of the bulk update, pass the returned `jobId` to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job).
 *
 * To update tags on specific invoices by ID, call [Bulk Update Invoice Tags](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoice-tags).
 * @param filter - Filter for selecting invoices to update.
 * @public
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId INVOICES.INVOICE_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.invoices.invoices.v4.Invoices.BulkUpdateInvoiceTagsByFilter
 */
export async function bulkUpdateInvoiceTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateInvoiceTagsByFilterOptions
): Promise<
  NonNullablePaths<BulkUpdateInvoiceTagsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdateInvoiceTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    {
      filter: filter,
      assignTags: options?.assignTags,
      unassignTags: options?.unassignTags,
    },
    []
  );

  const reqOpts =
    ambassadorWixInvoicesInvoicesV4Invoice.bulkUpdateInvoiceTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateInvoiceTagsByFilterOptions {
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}
